/**
 * Wave 27.0 — Project Detail (full-page editor).
 *
 * Three columns on desktop (header full-width):
 *   Left  : Linked entities (jobs / quotes / orders / invoices / credit notes / sites / meetings / tasks / tickets)
 *           Each group has a "+ Link" button → opens type-ahead picker.
 *   Right : P&L summary (manager+), Expense capture, Quick actions, Settings.
 *   Below : Activity timeline.
 *
 * Mobile collapses to a stacked single column with the linked-items tabs at the top.
 */
import { ProjectScorecardTab } from "../components/scorecard/ProjectScorecardTab";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Briefcase, Loader2, ChevronDown, ChevronRight, Search, X, Plus, Tag,
  ClipboardList, FileText, Receipt as ReceiptIcon, FileMinus, MapPin,
  Calendar as CalendarIcon, CheckSquare, LifeBuoy, Edit3, ExternalLink,
  TrendingUp, TrendingDown, AlertTriangle, Camera, Activity, DollarSign,
  Trash2, Check, Settings as SettingsIcon, RefreshCw, Inbox, ShoppingCart,
  Radar, Truck, BarChart3, FolderOpen, Upload, Link as LinkIcon, Download, Coins,
} from "lucide-react";
import api from "../lib/api";
import { kickOffAutoOcrBatch } from "../lib/autoOcr";
import { useAuth } from "../context/AuthContext";
import { toast } from "../components/ui/sonner";
import { EntityShell } from "../components/shell";
import HistoryBlock from "../components/shell/HistoryBlock";
import DatePickerInput from "../components/DatePickerInput";
import VoiceTextarea from "../components/VoiceTextarea";
import ProjectPlannerCard from "../components/projects/ProjectPlannerCard";
import ProjectPlanningSessions from "../components/projects/ProjectPlanningSessions";
import ReportsTechnicalSection from "../components/customer-blocks/ReportsTechnicalSection";  // used inside ProjectDocumentsCard
import ProjectPickingCard from "../components/projects/ProjectPickingCard";
import ProjectJobCardsCard from "../components/projects/ProjectJobCardsCard";
import ProjectProcurementCard from "../components/projects/ProjectProcurementCard";
import DictTextarea from "../components/DictTextarea";

const STATUS_COLORS = {
  planning: "#A78BFA", active: "#10B981", on_hold: "#F59E0B",
  complete: "#0EA5E9", archived: "#647087", cancelled: "#F43F5E",
};
const STATUS_LABEL = {
  planning: "Planning", active: "Active", on_hold: "On hold",
  complete: "Complete", archived: "Archived", cancelled: "Cancelled",
};

const LINK_GROUPS = [
  { kind: "jobcard",        label: "Job Cards",       icon: ClipboardList, accent: "#1FB6C1" },
  { kind: "quote",          label: "Quotes",          icon: FileText,      accent: "#A78BFA" },
  { kind: "sales_order",    label: "Sales Orders",    icon: FileText,      accent: "#0EA5E9" },
  { kind: "invoice",        label: "Invoices",        icon: ReceiptIcon,   accent: "#10B981" },
  { kind: "credit_note",    label: "Credit Notes",    icon: FileMinus,     accent: "#F43F5E" },
  { kind: "purchase_order", label: "Purchase Orders", icon: ShoppingCart,  accent: "#F59E0B", managerOnly: true },
  // Iter 420 — Procurement Trackers as a first-class linked group.
  // Deep-links to /procurement for full create/edit; the row shows
  // ETA + status inline so the Linked tab is the single source of truth.
  { kind: "procurement_tracker", label: "Procurement", icon: Truck,        accent: "#F59E0B", managerOnly: true },
  // Iter 420 — Picking Lists directly on Linked so users can jump into
  // any slip in one click. Full pick/return workflow stays on /picking.
  { kind: "picking_list",   label: "Picking",         icon: ClipboardList, accent: "#22D3EE" },
  { kind: "site",           label: "Sites",           icon: MapPin,        accent: "#22C55E" },
  { kind: "meeting",        label: "Meetings",        icon: CalendarIcon,  accent: "#06B6D4" },
  { kind: "task",           label: "Tasks",           icon: CheckSquare,   accent: "#EAB308" },
  { kind: "ticket",         label: "Tickets",         icon: LifeBuoy,      accent: "#F43F5E" },
];

const fmtMoney = (n, ccy = "ZAR") =>
  n == null
    ? "—"
    : `${ccy} ${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = (user?.role || "").toLowerCase();
  const isTech = role === "technician";
  const isManagerPlus = role === "owner" || role === "admin" || role === "manager";

  const [proj, setProj] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [timeline, setTimeline] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [linkPicker, setLinkPicker] = useState(null); // {kind}
  const [focusedPickingId, setFocusedPickingId] = useState(null);
  const [tab, setTab] = useState("linked");
  const [searchParams] = useSearchParams();

  // Hub deep-link: when user arrives via /projects/{pid}?picking={lid},
  // scroll the picking card into view and focus that list.
  useEffect(() => {
    const lid = searchParams.get("picking");
    if (lid) {
      setFocusedPickingId(lid);
      setTab("picking");
    }
  }, [searchParams]);
  const [editMode, setEditMode] = useState(false);

  // Defensive: don't show cost data until user resolved AND not a tech.
  const canSeeCosts = !!user && !isTech;

  const load = async () => {
    setLoading(true);
    setAccessDenied(false);
    try {
      const [{ data: p }, { data: t }, { data: e }] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/projects/${id}/timeline`),
        api.get(`/expenses`, { params: { project_id: id, limit: 50 } }),
      ]);
      setProj(p);
      setTimeline(t.items || []);
      setExpenses(e.items || []);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 403) {
        setAccessDenied(true);
      } else if (code === 404) {
        toast.error("Project not found.");
        navigate("/projects");
      } else {
        toast.error("Failed to load project.");
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const onLink = async (kind, ent) => {
    try {
      await api.post(`/projects/${id}/link`, { kind, id: ent.id });
      toast.success(`Linked ${kind}.`);
      setLinkPicker(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Link failed.");
    }
  };
  // Iter 424.n — Multi-link handler for the picker. Best-effort per row;
  // aggregates successes + failures.
  const onLinkMany = async (kind, ents) => {
    let ok = 0, fail = 0;
    for (const ent of ents) {
      try {
        await api.post(`/projects/${id}/link`, { kind, id: ent.id });
        ok += 1;
      } catch { fail += 1; }
    }
    if (ok) toast.success(`Linked ${ok} ${kind}${ok === 1 ? "" : "s"}.`);
    if (fail) toast.error(`Failed to link ${fail} row${fail === 1 ? "" : "s"}.`);
    setLinkPicker(null);
    load();
  };
  const [pendingUnlink, setPendingUnlink] = useState(null);  // {kind, eid}
  const onUnlink = (kind, eid) => {
    // Trust Contract Rule 2 — no window.confirm; arm an inline pending state
    // and surface a toast with "Confirm" CTA via the LinkGroupCard footer.
    setPendingUnlink({ kind, eid });
  };
  const confirmUnlink = async () => {
    if (!pendingUnlink) return;
    const { kind, eid } = pendingUnlink;
    try {
      await api.delete(`/projects/${id}/link/${kind}/${eid}`);
      toast.success(`Unlinked.`);
      setPendingUnlink(null);
      load();
    } catch (e) { toast.error("Unlink failed."); }
  };

  // [Iter 420] Flip the active-for-project flag on a quote/SO. Cycles
  // through True → False → null on click (see SalesDocActivePill).
  // Reloads to refresh both the row chip and the Money/Budget totals.
  const onSalesDocActiveChange = async (docId, nextVal) => {
    try {
      await api.patch(`/sales-documents/${docId}/project-active`, {
        is_active_for_project: nextVal,
      });
      toast.success(
        nextVal === true ? "Marked as Active — counts toward budget."
        : nextVal === false ? "Marked as Not-used — excluded from budget."
        : "Reset to Auto — falls back to status."
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update.");
    }
  };

  if (loading) return (
    <div className="hv-card p-10 text-center">
      <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#1FB6C1]" />
    </div>
  );
  if (accessDenied) return (
    <div className="hv-card p-10 text-center max-w-md mx-auto" data-testid="project-access-denied">
      <AlertTriangle className="w-10 h-10 mx-auto mb-2 text-amber-400" />
      <div className="font-display font-bold text-lg">No access to this project</div>
      <div className="text-xs text-[#647087] mt-1">
        Technicians only see projects where they own a linked Job Card. Ask an admin to assign you, or head back to the list.
      </div>
      <button onClick={() => navigate("/projects")} className="hv-btn hv-btn-primary text-xs mt-4 inline-flex" data-testid="proj-access-denied-back">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to projects
      </button>
    </div>
  );
  if (!proj) return null;

  const linksByKind = proj.links || {};
  const burn = proj.budget_burn_pct;
  const ccy = proj.budget_currency || "ZAR";
  const burnColor =
    burn == null ? "#647087"
    : burn < 70 ? "#10B981"
    : burn < 90 ? "#F59E0B"
    : burn < 100 ? "#F43F5E"
    : "#DC2626";

  // Phase 7 Sub-iter 7E — EntityShell migration.
  const STATUS_TONE = {
    planning: "violet",
    active: "green",
    on_hold: "amber",
    complete: "blue",
    archived: "slate",
    cancelled: "red",
  };
  const projectTabs = [
    { key: "linked", label: "Linked" },
    { key: "planner", label: "Planning & Reports" },
    { key: "jobcards", label: "Job Cards" },
    { key: "picking", label: "Picking" },
    { key: "procurement", label: "Procurement" },
    { key: "documents", label: "Documents" },
    canSeeCosts && { key: "money", label: "Financial" },
    { key: "scorecard", label: "Scorecard" },
    { key: "activity", label: "Activity" },
  ].filter(Boolean);
  const headerActions = (
    <>
      {!isTech && (
        <Link
          to={`/projects/${id}/dashboard`}
          className="hv-btn hv-btn-ghost text-xs px-2"
          data-testid="proj-dashboard-btn"
          title="See budgets, schedule, trips, productivity KPIs"
          aria-label="Dashboard"
        >
          <BarChart3 className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Dashboard</span>
        </Link>
      )}
      {!isTech && (
        <button
          onClick={() => setEditMode((v) => !v)}
          className={`hv-btn text-xs px-2 ${editMode ? "hv-btn-primary" : "hv-btn-ghost"}`}
          data-testid="proj-edit-toggle"
          title={editMode ? "Stop editing" : "Edit project"}
          aria-label={editMode ? "Done" : "Edit"}
        >
          <Edit3 className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{editMode ? "Done" : "Edit"}</span>
        </button>
      )}
    </>
  );

  return (
    <EntityShell
      shortId={proj.short_id ? `#${proj.short_id}` : undefined}
      kindLabel="Project"
      title={proj.title || "Project"}
      status={{ label: STATUS_LABEL[proj.status] || proj.status, tone: STATUS_TONE[proj.status] || "slate" }}
      customerLink={proj.customer_id ? {
        label: `View customer · ${proj.customer_name || "—"}`,
        to: `/customers/${proj.customer_id}`,
      } : undefined}
      backTo="/projects"
      actions={headerActions}
      tabs={projectTabs}
      activeTab={tab}
      onTabChange={setTab}
    >
    <div className="space-y-4" data-testid="project-detail-page">
      {/* Inline meta line (status pill changer + owner + planned dates). */}
      <div className="hv-card p-3 flex items-center gap-3 flex-wrap text-xs" data-testid="proj-meta-strip">
        <StatusPill
          status={proj.status}
          onChange={(s) => api.patch(`/projects/${id}`, { status: s }).then(load)}
          editable={isManagerPlus && !isTech}
        />
        {proj.owner_name && <span className="text-[#a0aabf]">Owned by {proj.owner_name}</span>}
        {proj.planned_start && (
          <span className="text-[#a0aabf]">
            {proj.planned_start}{proj.planned_end ? ` → ${proj.planned_end}` : ""}
          </span>
        )}
      </div>

      {/* Inline 2-step unlink confirmation (Trust Contract Rule 2). */}
      {pendingUnlink && (
        <div
          className="hv-card border-rose-500/40 bg-rose-500/5 p-3 flex items-center justify-between gap-2"
          data-testid="proj-unlink-confirm"
        >
          <span className="text-xs text-rose-200">
            Unlink this <strong>{pendingUnlink.kind}</strong> from the project?
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPendingUnlink(null)}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="proj-unlink-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmUnlink}
              className="hv-btn hv-btn-primary text-xs bg-rose-500 hover:bg-rose-400"
              data-testid="proj-unlink-confirm-btn"
            >
              <Trash2 className="w-3 h-3" /> Confirm unlink
            </button>
          </div>
        </div>
      )}

      {/* Edit panel — common to all tabs since it edits the canonical row. */}
      {editMode && !isTech && (
        <EditPanel proj={proj} onSaved={() => { setEditMode(false); load(); }} />
      )}

      {tab === "linked" && (
        <div className="space-y-3" data-testid="proj-tab-linked">
          {LINK_GROUPS.filter((g) => !(g.managerOnly && !isManagerPlus)).map((g) => {
            const items = linksByKind[g.kind] || [];
            return (
              <LinkGroupCard
                key={g.kind}
                group={g}
                items={items}
                onAdd={() => setLinkPicker(g.kind)}
                onRemove={(eid) => onUnlink(g.kind, eid)}
                isTech={isTech}
                projectId={proj.id}
                onActiveChange={onSalesDocActiveChange}
              />
            );
          })}
        </div>
      )}

      {tab === "planner" && (
        <div data-testid="proj-tab-planner">
          <ProjectPlannerCard
            projectId={proj.id}
            customerId={proj.customer_id}
          />
          {/* Iter 428.p4 — Additional non-proposal workbench sessions
              (planning / assessment) spawned from Engineering Desk. */}
          <ProjectPlanningSessions projectId={proj.id} />
        </div>
      )}

      {tab === "jobcards" && (
        <div data-testid="proj-tab-jobcards">
          <ProjectJobCardsCard projectId={proj.id} />
        </div>
      )}

      {tab === "picking" && (
        <div data-testid="proj-tab-picking">
          <ProjectPickingCard
            projectId={proj.id}
            focusListId={focusedPickingId}
          />
        </div>
      )}

      {tab === "procurement" && (
        <div data-testid="proj-tab-procurement">
          <ProjectProcurementCard projectId={proj.id} />
        </div>
      )}

      {tab === "documents" && (
        <div data-testid="proj-tab-documents">
          <ProjectDocumentsCard
            projectId={proj.id}
            customerId={proj.customer_id}
            isTech={isTech}
          />
        </div>
      )}

      {tab === "money" && canSeeCosts && (
        <div className="space-y-3" data-testid="proj-tab-money">
          <PnlCard proj={proj} burn={burn} burnColor={burnColor} />
          {/* Iter 425.k (Dirk, 2026-03-01) — Foldable financial cards.
              Each card can be collapsed independently; the open state
              persists in localStorage so ops don't have to re-open
              every visit. */}
          <FoldableFinancialCard
            title="Profitability & budget breakdown"
            storageKey="proj-fin-fold-profitability"
            defaultOpen={true}
            testId="proj-fold-profitability"
          >
            <ProfitabilityLinesCard projectId={proj.id} ccy={ccy} />
          </FoldableFinancialCard>
          <FoldableFinancialCard
            title="Spent vs budget"
            storageKey="proj-fin-fold-spent"
            defaultOpen={false}
            testId="proj-fold-spent"
          >
            <SpentBreakdownCard projectId={proj.id} ccy={ccy} onChange={load} />
          </FoldableFinancialCard>
          <BudgetItemsCard projectId={proj.id} ccy={ccy} onChange={load} />
          <ExpensesCard
            proj={proj}
            expenses={expenses}
            onReload={load}
            isManagerPlus={isManagerPlus}
            isTech={isTech}
            canSeeCosts={canSeeCosts}
          />
        </div>
      )}

      {tab === "scorecard" && <ProjectScorecardTab projectId={id} />}
      {tab === "activity" && (
        <div className="space-y-3" data-testid="proj-tab-activity">
          <TimelineSection timeline={timeline} />
          <HistoryBlock entity={{ id, kind: "project" }} />
        </div>
      )}

      {/* Link picker drawer — full-screen, not a modal (no overlay dim). */}
      {linkPicker && (
        <LinkPicker
          kind={linkPicker}
          customerId={proj.customer_id}
          onClose={() => setLinkPicker(null)}
          onPick={(ent) => onLink(linkPicker, ent)}
          onPickMany={(ents) => onLinkMany(linkPicker, ents)}
        />
      )}
    </div>
    </EntityShell>
  );
}

function StatusPill({ status, onChange, editable }) {
  const [open, setOpen] = useState(false);
  const c = STATUS_COLORS[status] || "#647087";
  if (!editable) {
    return (
      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold"
        style={{ background: `${c}22`, color: c }}>
        {STATUS_LABEL[status] || status}
      </span>
    );
  }
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold inline-flex items-center gap-1"
        style={{ background: `${c}22`, color: c }}
        data-testid="proj-status-pill"
      >
        {STATUS_LABEL[status] || status} <ChevronDown className="w-2.5 h-2.5" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 left-0 bg-[#0e1015] border border-white/10 rounded shadow-xl min-w-[140px] py-1">
          {Object.keys(STATUS_LABEL).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => { onChange(s); setOpen(false); }}
              className="block w-full text-left text-xs px-2 py-1 hover:bg-white/[0.04]"
              style={{ color: STATUS_COLORS[s] }}
              data-testid={`proj-status-set-${s}`}
            >
              {STATUS_LABEL[s]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function EditPanel({ proj, onSaved }) {
  const [f, setF] = useState({
    title: proj.title || "",
    description: proj.description || "",
    planned_start: proj.planned_start || "",
    planned_end: proj.planned_end || "",
    budget_source: proj.budget_source || "manual",
    budget_amount: proj.budget_amount ?? "",
    budget_currency: proj.budget_currency || "ZAR",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/projects/${proj.id}`, {
        title: f.title || null,
        description: f.description,
        planned_start: f.planned_start || null,
        planned_end: f.planned_end || null,
        budget_source: f.budget_source,
        budget_amount: f.budget_amount === "" ? null : Number(f.budget_amount),
        budget_currency: f.budget_currency,
      });
      toast.success("Project updated.");
      onSaved();
    } catch (e) { toast.error("Update failed."); }
    finally { setBusy(false); }
  };
  return (
    <div className="hv-card !p-3 space-y-2" data-testid="proj-edit-panel">
      <input
        value={f.title}
        onChange={(e) => setF({ ...f, title: e.target.value })}
        className="hv-input text-sm w-full"
        placeholder="Title"
      />
      <VoiceTextarea
        value={f.description}
        onChange={(v) => setF({ ...f, description: v })}
        rows={2}
        className="text-sm w-full"
        placeholder="Notes…"
        context="project_micronote"
        testIdPrefix="project-note"
      />
      <div className="grid grid-cols-2 gap-2">
        <DatePickerInput value={f.planned_start || ""} onChange={(v) => setF({ ...f, planned_start: v })} />
        <DatePickerInput value={f.planned_end || ""} onChange={(v) => setF({ ...f, planned_end: v })} />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <select value={f.budget_source} onChange={(e) => setF({ ...f, budget_source: e.target.value })} className="hv-input text-xs w-full" data-testid="proj-edit-budget-source">
          <option value="manual">Manual</option>
          <option value="auto">Auto (invoice → SO → quote → PO)</option>
          <option value="active_docs">Union of active docs (SO + Quote + PO + Invoice)</option>
          <option value="invoice_total">Active invoices only</option>
          <option value="quote_total">Active quotes only</option>
          <option value="sales_order_total">Active sales orders only</option>
          <option value="purchase_order_total">Active purchase orders only</option>
        </select>
        <select value={f.budget_currency} onChange={(e) => setF({ ...f, budget_currency: e.target.value })} className="hv-input text-xs w-full">
          {["ZAR", "USD", "EUR", "GBP"].map((c) => <option key={c}>{c}</option>)}
        </select>
        <input
          type="number"
          step="0.01"
          value={f.budget_amount}
          onChange={(e) => setF({ ...f, budget_amount: e.target.value })}
          placeholder="Budget"
          disabled={f.budget_source !== "manual"}
          className="hv-input text-xs w-full disabled:opacity-50"
        />
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={save} disabled={busy} className="hv-btn hv-btn-primary text-xs disabled:opacity-50" data-testid="proj-edit-save">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save
        </button>
      </div>
    </div>
  );
}

function LinkGroupCard({ group, items, onAdd, onRemove, isTech, projectId, onActiveChange }) {
  const Icon = group.icon;
  const newRoute = createNewRouteFor(group.kind, projectId);
  return (
    <div className="hv-card !p-3" data-testid={`proj-group-${group.kind}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 font-display font-semibold text-sm" style={{ color: group.accent }}>
          <Icon className="w-3.5 h-3.5" />
          {group.label}
          <span className="text-[10px] text-[#647087] font-mono">({items.length})</span>
        </div>
        {!isTech && (
          <div className="flex items-center gap-1">
            <button onClick={onAdd} className="hv-btn hv-btn-ghost text-[11px]" data-testid={`proj-add-${group.kind}`}>
              <LinkIcon className="w-3 h-3" /> Link
            </button>
            {newRoute && (
              <Link
                to={newRoute}
                className="hv-btn hv-btn-ghost text-[11px]"
                data-testid={`proj-new-${group.kind}`}
                title={`Create a new ${group.label.toLowerCase().replace(/s$/, "")} and link to this project`}
              >
                <Plus className="w-3 h-3" /> New
              </Link>
            )}
          </div>
        )}
      </div>
      {items.length === 0 ? (
        <div className="text-[11px] text-[#647087] italic">None linked yet.</div>
      ) : (
        <ul className="space-y-1">
          {items.map((it) => (
            <li
              key={`${it.kind}-${it.id}`}
              className="flex items-center justify-between gap-2 text-xs px-2 py-1 rounded hover:bg-white/[0.03]"
              data-testid={`proj-item-${group.kind}-${it.id}`}
            >
              <Link to={routeFor(it.kind, it.id)} className="flex-1 min-w-0 flex items-center gap-2">
                <span className="font-mono text-[#1FB6C1]">{it.label}</span>
                {it.title && <span className="text-white truncate">— {it.title}</span>}
                {/* Iter 424.m — DNU / INTERNAL badge so users can spot
                    the engineering copy that carries the SKUs, and pick
                    it as Active for revenue+budget. */}
                {(it.audience === "internal" || it.audience === "dnu") && (
                  <span
                    className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#F59E0B]/20 text-[#F59E0B] border border-[#F59E0B]/40"
                    title="Internal / DNU (Do Not Use externally). Engineering copy with SKUs. Pick this as ACTIVE to drive SKU-based budget matching."
                    data-testid={`proj-item-${group.kind}-${it.id}-dnu`}
                  >
                    DNU
                  </span>
                )}
                {it.status && <span className="text-[9px] uppercase px-1 rounded bg-white/[0.06] text-[#a0aabf]">{it.status}</span>}
                {/* [Iter 420] Family-tree provenance chip. */}
                {it.linked_via && typeof it.linked_via === "object" && it.linked_via.kind && it.linked_via.kind !== "direct" && (
                  <span
                    className="text-[9px] px-1 py-0.5 rounded bg-[#A78BFA]/15 text-[#A78BFA]"
                    title={`Family-tree link via ${it.linked_via.kind}`}
                    data-testid={`proj-item-${group.kind}-${it.id}-linkedvia`}
                  >
                    via {it.linked_via.kind === "picking_list" ? "picking" :
                          it.linked_via.kind === "jobcard" ? "JC" :
                          it.linked_via.kind === "sales_document" ? "sales doc" :
                          it.linked_via.kind}
                  </span>
                )}
                {it.amount != null && (
                  <span className="text-[10px] font-mono text-[#10B981] ml-auto">{fmtMoney(it.amount, it.currency || "ZAR")}</span>
                )}
              </Link>
              {/* [Iter 420 + 424.k] Active-flag toggle for revenue/budget
                  side docs: quotes, sales_orders, invoices. Once ANY doc in a
                  category is set ACTIVE, only those active ones count.
                  Purchase orders always count as spend (Batch 5). */}
              {(group.kind === "quote" || group.kind === "sales_order" || group.kind === "invoice") && !isTech && (
                <SalesDocActivePill it={it} onChange={onActiveChange} />
              )}
              {!isTech && (
                <button
                  onClick={() => onRemove(it.id)}
                  className="text-[#647087] hover:text-rose-400 p-0.5"
                  data-testid={`proj-unlink-${group.kind}-${it.id}`}
                  aria-label="Unlink"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// [Iter 420 + 424.k] Compact chip that shows the active-for-project state.
// States: Active (green) → Not used (red) → Auto (grey) → Active …
// Made unmissable per user brief 2026-02-28: label is explicit, not "AUTO",
// and hover shows the next action so the toggle is discoverable.
function SalesDocActivePill({ it, onChange }) {
  const s = it.is_active_for_project;
  const label = s === true ? "ACTIVE" : s === false ? "NOT USED" : "AUTO";
  const cls = s === true
    ? "bg-emerald-500/25 text-emerald-200 border-emerald-400/60 shadow-sm shadow-emerald-500/20"
    : s === false
    ? "bg-rose-500/20 text-rose-200 border-rose-500/50"
    : "bg-amber-500/10 text-amber-200 border-amber-500/40";
  const nextVal = s === true ? false : s === false ? null : true;
  const nextLabel = nextVal === true ? "Set ACTIVE"
                  : nextVal === false ? "Mark NOT USED"
                  : "Reset to AUTO";
  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onChange && onChange(it.id, nextVal); }}
      className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border ${cls} hover:brightness-125 transition-all cursor-pointer`}
      title={`Currently ${label}. Click → ${nextLabel}.\nOnly ACTIVE docs count for revenue + budget. AUTO = fall back to status.`}
      data-testid={`proj-sd-${it.id}-active-pill`}
    >
      {label}
    </button>
  );
}

function routeFor(kind, id) {
  switch (kind) {
    case "jobcard": return `/jobcards/${id}`;
    case "ticket": return `/tickets/${id}`;
    case "site": return `/customers`;
    case "meeting": return `/meetings/${id}`;
    case "task": return `/tasks`;
    case "procurement_tracker": return `/procurement/${id}`;
    case "picking_list": return `/picking/lists/${id}`;
    default: return `/customers`;
  }
}

// Iter 420 — Some link groups have a "Create new" companion action that
// deep-links into their dedicated module with the project pre-filled.
// Returning null hides the button (existing pure-picker behaviour).
function createNewRouteFor(kind, projectId) {
  switch (kind) {
    case "procurement_tracker": return `/procurement?new=1&project_id=${projectId}`;
    case "picking_list":        return `/picking?new=1&project_id=${projectId}`;
    default: return null;
  }
}

function PnlCard({ proj, burn, burnColor }) {
  const ccy = proj.budget_currency || "ZAR";
  const forecast = proj.forecast_over_at;
  const dailyBurn = proj.daily_burn;
  const statusBand = proj.status_band || "on_track";
  const bandLabel = {
    on_track: "On track", caution: "Watch", warning: "At risk", over: "Over budget",
  }[statusBand];
  return (
    <div className="hv-card !p-3" data-testid="proj-pnl-card">
      <div className="flex items-center justify-between mb-2">
        <div className="font-display font-semibold text-sm text-[#a0aabf] inline-flex items-center gap-1.5">
          <DollarSign className="w-3.5 h-3.5" /> Profitability
        </div>
        <span
          className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-bold"
          style={{ background: `${burnColor}22`, color: burnColor }}
          data-testid="proj-status-band"
        >
          {bandLabel}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <Stat label="Revenue" value={fmtMoney(proj.revenue_total, ccy)} color="#10B981" />
        <Stat
          label="Spend budget"
          value={fmtMoney(
            proj.spend_budget != null ? proj.spend_budget : proj.effective_budget,
            ccy,
          )}
          color="#F59E0B"
        />
        <Stat
          label="WIP (spent to date)"
          value={fmtMoney(proj.wip_total != null ? proj.wip_total : (proj.cost_total ?? proj.expense_total), ccy)}
          color="#A78BFA"
        />
        <Stat
          label="Running margin"
          value={fmtMoney(
            proj.running_margin != null ? proj.running_margin : proj.margin_amount,
            ccy,
          )}
          color={(proj.running_margin ?? proj.margin_amount ?? 0) >= 0 ? "#10B981" : "#F43F5E"}
          icon={(proj.running_margin ?? proj.margin_amount ?? 0) >= 0 ? TrendingUp : TrendingDown}
        />
      </div>
      {(proj.spend_budget ?? proj.effective_budget) > 0 && (
        <div className="mt-2">
          <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden relative">
            <div
              className="h-full transition-all"
              style={{
                width: `${Math.min(100, proj.burn_pct != null ? proj.burn_pct : (burn || 0))}%`,
                background: burnColor,
              }}
            />
            {/* Threshold ticks at 70 and 90 */}
            <div className="absolute top-0 bottom-0 w-px bg-white/15" style={{ left: "70%" }} />
            <div className="absolute top-0 bottom-0 w-px bg-white/15" style={{ left: "90%" }} />
          </div>
          <div className="flex items-center justify-between mt-1 text-[10px]">
            <span style={{ color: burnColor }} data-testid="proj-burn-label">
              {proj.burn_pct != null ? `${proj.burn_pct}% budget spent (WIP)` : (burn != null ? `${burn}% spent` : "—")}
            </span>
            <span
              className="text-[#647087]"
              title={`Planned margin: ${fmtMoney(proj.planned_margin, ccy)} (${proj.planned_margin_pct ?? 0}%)`}
              data-testid="proj-planned-margin"
            >
              {proj.planned_margin_pct != null
                ? `${proj.planned_margin_pct}% planned · ${proj.running_margin_pct ?? proj.margin_pct ?? 0}% running`
                : ""}
            </span>
          </div>
        </div>
      )}
      {/* Phase A — burn forecast row */}
      {(forecast || dailyBurn) && (
        <div className="mt-2 text-[10px] text-[#a0aabf] inline-flex items-center gap-1.5 flex-wrap" data-testid="proj-forecast-row">
          <Activity className="w-3 h-3 text-[#1FB6C1]" />
          {dailyBurn != null && (
            <span><span className="text-white font-mono">{fmtMoney(dailyBurn, ccy)}</span>/day burn</span>
          )}
          {forecast && (
            <span>
              · projected over budget on <span className={statusBand === "warning" || statusBand === "over" ? "text-rose-400" : "text-amber-300"}>{forecast}</span>
              {proj.days_to_overrun != null && proj.days_to_overrun > 0 && (
                <span className="text-[#647087]"> ({proj.days_to_overrun}d)</span>
              )}
            </span>
          )}
        </div>
      )}
      {proj.pending_expenses_count > 0 && (
        <div className="mt-2 text-[10px] text-amber-300 inline-flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" /> {proj.pending_expenses_count} receipt{proj.pending_expenses_count === 1 ? "" : "s"} awaiting review
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color, icon: Icon }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-[#647087]">{label}</div>
      <div className="font-mono text-sm inline-flex items-center gap-0.5" style={{ color }}>
        {Icon && <Icon className="w-3 h-3" />}{value}
      </div>
    </div>
  );
}


// [Iter 420 — 2026-03-01] Money-tab drill-down: per-line profitability
// from linked quotes/SOs/invoices. Shows Qty · Unit price · Revenue ·
// Unit cost (from inventory or per-project override) · Line margin · GP%.
// Managers can inline-edit the unit cost — the override is stored on
// `project_cost_overrides` (project-scoped, does NOT touch global
// inventory).
// Iter 425.k (Dirk, 2026-03-01) — Slim collapsible wrapper for the
// three heavy financial cards on the Money tab. Each card has its own
// internal header, so this wrapper renders only a chevron bar on
// collapse and the child below on expand. Open state persists in
// localStorage so ops don't have to re-open every visit.
function FoldableFinancialCard({ title, storageKey, defaultOpen = false, testId, children }) {
  const [open, setOpen] = useState(() => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw === "1") return true;
      if (raw === "0") return false;
    } catch { /* ignore */ }
    return defaultOpen;
  });
  const toggle = () => {
    setOpen((v) => {
      const next = !v;
      try { window.localStorage.setItem(storageKey, next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };
  return (
    <div className="space-y-1.5" data-testid={testId} data-fold-state={open ? "open" : "closed"}>
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center gap-2 px-3 py-1.5 rounded border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] text-left transition-colors"
        data-testid={`${testId}-toggle`}
      >
        {open
          ? <ChevronDown className="w-3.5 h-3.5 text-[#647087] shrink-0" />
          : <ChevronRight className="w-3.5 h-3.5 text-[#647087] shrink-0" />}
        <span className="text-[11px] uppercase tracking-wider font-bold text-[#a0aabf]">
          {title}
        </span>
        {!open && (
          <span className="text-[10px] text-[#647087] ml-auto">Click to expand</span>
        )}
      </button>
      {open && children}
    </div>
  );
}


function ProfitabilityLinesCard({ projectId, ccy }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState(null);
  const [draftCost, setDraftCost] = useState("");
  const [saving, setSaving] = useState(false);
  // Iter 425.l Step 3 — Inline budget edit (override_amount on
  // project_line_overrides). Keyed independently from cost edits so
  // both can coexist on the same row.
  const [editingBudgetKey, setEditingBudgetKey] = useState(null);
  const [draftBudget, setDraftBudget] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Iter 425.l Step 2 — Single source of truth: /financial-lines
      // fuses pnl-lines + budget-breakdown + spent-breakdown so we get
      // revenue, cost, effective budget, and routed spend in one call.
      const { data } = await api.get(`/projects/${projectId}/financial-lines`);
      setData(data);
    } catch (e) {
      // Non-fatal — tech users get 403, manager+ shouldn't.
      setData({ mode: "empty", lines: [], totals: {} });
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (line) => {
    // Iter 425.a — key on (doc_id, line_index) so identical SKUs across
    // different quote/SO lines are edited independently.
    const key = line.line_key
      || (line.doc_id && line.line_index != null ? `${line.doc_id}::${line.line_index}` : null)
      || line.matched_inventory_id
      || `desc:${line.description}`;
    setEditingKey(key);
    setDraftCost(String(line.unit_cost ?? ""));
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setDraftCost("");
  };

  const saveOverride = async (line) => {
    const val = parseFloat(draftCost);
    if (!Number.isFinite(val) || val < 0) {
      toast.error("Enter a valid cost (≥ 0).");
      return;
    }
    setSaving(true);
    try {
      const body = {
        unit_cost: val,
        // Iter 425.a — Send line-specific keys so the override pins to
        // ONE line only. Fallback to SKU/description if the backend
        // response somehow omitted line_index (shouldn't happen after
        // Iter 425.a but keep parity with legacy payloads).
        doc_id: line.doc_id || null,
        line_index: (line.line_index != null ? line.line_index : null),
        matched_inventory_id: line.matched_inventory_id || null,
        description: line.description || "",
      };
      await api.post(`/projects/${projectId}/cost-overrides`, body);
      toast.success("Cost override saved for this line.");
      cancelEdit();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save override.");
    } finally {
      setSaving(false);
    }
  };

  const clearOverride = async (line) => {
    if (!line.override_id) return;
    if (!window.confirm("Remove this project-specific cost and go back to the inventory cost?")) return;
    try {
      await api.delete(`/projects/${projectId}/cost-overrides/${line.override_id}`);
      toast.success("Override removed.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove override.");
    }
  };

  // Iter 425.l Step 3 — Inline budget override save / clear. Uses
  // PATCH /projects/{pid}/budget-lines/{doc_id}/{line_index}.
  const startBudgetEdit = (line) => {
    const key = line.line_key || `${line.doc_id}::${line.line_index}`;
    setEditingBudgetKey(key);
    setDraftBudget(String(line.effective_amount ?? line.source_amount ?? 0));
  };
  const cancelBudgetEdit = () => {
    setEditingBudgetKey(null);
    setDraftBudget("");
  };
  const saveBudgetOverride = async (line) => {
    const val = parseFloat(draftBudget);
    if (!Number.isFinite(val) || val < 0) {
      toast.error("Enter a valid budget amount (≥ 0).");
      return;
    }
    if (!line.doc_id || line.line_index == null) {
      toast.error("This line has no linked document — cannot pin a budget override.");
      return;
    }
    setSaving(true);
    try {
      await api.patch(
        `/projects/${projectId}/budget-lines/${line.doc_id}/${line.line_index}`,
        { override_amount: val }
      );
      toast.success("Budget override saved for this line.");
      cancelBudgetEdit();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save budget override.");
    } finally {
      setSaving(false);
    }
  };
  const clearBudgetOverride = async (line) => {
    if (line.override_amount == null) return;
    if (!window.confirm("Remove this project-specific budget override and go back to the document's source amount?")) return;
    setSaving(true);
    try {
      await api.delete(
        `/projects/${projectId}/budget-lines/${line.doc_id}/${line.line_index}`
      );
      toast.success("Budget override cleared.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear budget override.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="hv-card !p-3 flex items-center gap-2 text-[11px] text-[#647087]" data-testid="proj-profitability-lines-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Calculating profitability…
      </div>
    );
  }

  const lines = data?.lines || [];
  const mode = data?.mode || "empty";
  const totals = data?.totals || {};

  return (
    <div className="hv-card !p-3" data-testid="proj-profitability-lines">
      <div className="flex items-center justify-between mb-2">
        <div className="font-display font-semibold text-sm text-[#a0aabf] inline-flex items-center gap-1.5">
          <BarChart3 className="w-3.5 h-3.5" /> Profitability breakdown
          {mode === "wip" && (
            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 ml-1">WIP · from quotes/SOs</span>
          )}
          {mode === "final" && (
            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 ml-1">Final · from invoices</span>
          )}
        </div>
        <button onClick={load} className="text-[10px] text-[#647087] hover:text-white inline-flex items-center gap-1" data-testid="proj-profitability-refresh">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {lines.length === 0 ? (
        <div className="text-[11px] text-[#647087] italic">
          No line items yet. Link a quote, sales order or invoice with itemised lines to see per-line revenue vs cost here.
        </div>
      ) : (
        <>
          <div className="overflow-x-auto hv-scrollbar-thin">
            <table
              className="w-full text-[11px] min-w-[640px]"
              data-testid="proj-profitability-table"
            >
              <thead>
                <tr className="text-[9px] uppercase tracking-wider text-[#647087] border-b border-white/[0.06]">
                  <th className="text-left py-1.5 pr-2">Item</th>
                  <th className="text-right py-1.5 px-1.5">Qty</th>
                  <th className="text-right py-1.5 px-1.5">Sell</th>
                  <th className="text-right py-1.5 px-1.5">Revenue</th>
                  <th className="text-right py-1.5 px-1.5">Unit cost</th>
                  <th className="text-right py-1.5 px-1.5">Line cost</th>
                  <th
                    className="text-right py-1.5 px-1.5 text-[#F59E0B]"
                    title="Effective budget for this line. Editable — pin a project-specific override or fall back to the source doc amount."
                  >
                    Budget
                  </th>
                  <th
                    className="text-right py-1.5 px-1.5 text-[#A78BFA]"
                    title="Actual spend routed to this line from PO / procurement / picking / expense (distributed across sister lines in the same bucket)."
                  >
                    Spent
                  </th>
                  <th
                    className="text-right py-1.5 px-1.5 text-[#94A3B8]"
                    title="Budget – Spent."
                  >
                    Headroom
                  </th>
                  <th className="text-right py-1.5 px-1.5">Margin</th>
                  <th className="text-right py-1.5 pl-1.5">GP%</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((ln, idx) => {
                  // Iter 425.a — Prefer line-specific key so two rows with
                  // the same SKU on different lines don't collapse into
                  // one edit target.
                  const key = ln.line_key
                    || (ln.doc_id && ln.line_index != null ? `${ln.doc_id}::${ln.line_index}` : null)
                    || ln.matched_inventory_id
                    || `desc:${ln.description}:${idx}`;
                  const editing = editingKey === key;
                  // Iter 425.a — Item column now shows the SKU / name
                  // on line 1 and the full line description on line 2
                  // so ops can distinguish identical-SKU lines that
                  // carry different specs / project scopes.
                  const primary = ln.inventory_sku || ln.inventory_name || ln.description || "—";
                  const secondary = (ln.inventory_sku || ln.inventory_name) ? ln.description : null;
                  const marginPos = (ln.line_margin || 0) >= 0;
                  return (
                    <tr key={key} className="border-b border-white/[0.03] hover:bg-white/[0.02]" data-testid={`proj-pline-${idx}`}>
                      <td className="py-1.5 pr-2">
                        <div className="text-white truncate max-w-[220px]" title={primary}>{primary}</div>
                        {secondary && (
                          <div className="text-[10px] text-[#a0aabf] truncate max-w-[220px]" title={secondary} data-testid={`proj-pline-desc-${idx}`}>
                            {secondary}
                          </div>
                        )}
                        <div className="text-[9px] text-[#647087]">
                          {ln.doc_number ? `${ln.doc_kind} · ${ln.doc_number}` : ln.doc_kind}
                          {ln.cost_source === "override" && (
                            <span className="ml-1.5 text-amber-300">· overridden</span>
                          )}
                          {ln.cost_source === "unknown" && (
                            <span className="ml-1.5 text-rose-300">· no cost linked</span>
                          )}
                        </div>
                      </td>
                      <td className="py-1.5 px-1.5 text-right font-mono text-[#a0aabf]">{ln.quantity}</td>
                      <td className="py-1.5 px-1.5 text-right font-mono text-[#a0aabf]">{fmtMoney(ln.unit_price, ccy)}</td>
                      <td className="py-1.5 px-1.5 text-right font-mono text-emerald-300">{fmtMoney(ln.line_revenue, ccy)}</td>
                      <td className="py-1.5 px-1.5 text-right">
                        {editing ? (
                          <div className="inline-flex items-center gap-1" data-testid={`proj-pline-edit-${idx}`}>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={draftCost}
                              onChange={(e) => setDraftCost(e.target.value)}
                              className="hv-input !py-0.5 !px-1 w-20 text-right font-mono text-[11px]"
                              autoFocus
                              disabled={saving}
                              data-testid={`proj-pline-cost-input-${idx}`}
                            />
                            <button onClick={() => saveOverride(ln)} disabled={saving} className="text-emerald-300 hover:text-emerald-200 disabled:opacity-50" data-testid={`proj-pline-cost-save-${idx}`}>
                              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                            </button>
                            <button onClick={cancelEdit} disabled={saving} className="text-[#647087] hover:text-white disabled:opacity-50" data-testid={`proj-pline-cost-cancel-${idx}`}>
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => startEdit(ln)}
                            className="font-mono text-[#a0aabf] hover:text-white inline-flex items-center gap-1"
                            title="Click to override cost for this project"
                            data-testid={`proj-pline-cost-btn-${idx}`}
                          >
                            {fmtMoney(ln.unit_cost, ccy)}
                            <Edit3 className="w-2.5 h-2.5 opacity-50" />
                          </button>
                        )}
                      </td>
                      <td className="py-1.5 px-1.5 text-right font-mono text-rose-300">{fmtMoney(ln.line_cost, ccy)}</td>
                      <td
                        className="py-1.5 px-1.5 text-right font-mono text-[#F59E0B]"
                        data-testid={`proj-pline-budget-${idx}`}
                      >
                        {editingBudgetKey === key ? (
                          <div className="inline-flex items-center gap-1" data-testid={`proj-pline-budget-edit-${idx}`}>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={draftBudget}
                              onChange={(e) => setDraftBudget(e.target.value)}
                              className="hv-input !py-0.5 !px-1 w-24 text-right font-mono text-[11px]"
                              autoFocus
                              disabled={saving}
                              data-testid={`proj-pline-budget-input-${idx}`}
                            />
                            <button
                              onClick={() => saveBudgetOverride(ln)}
                              disabled={saving}
                              className="text-emerald-300 hover:text-emerald-200 disabled:opacity-50"
                              data-testid={`proj-pline-budget-save-${idx}`}
                            >
                              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                            </button>
                            <button
                              onClick={cancelBudgetEdit}
                              disabled={saving}
                              className="text-[#647087] hover:text-white disabled:opacity-50"
                              data-testid={`proj-pline-budget-cancel-${idx}`}
                            >
                              <X className="w-3 h-3" />
                            </button>
                            {ln.override_amount != null && (
                              <button
                                onClick={() => clearBudgetOverride(ln)}
                                disabled={saving}
                                className="text-rose-300 hover:text-rose-200 disabled:opacity-50"
                                title="Clear override — use the source document amount"
                                data-testid={`proj-pline-budget-clear-${idx}`}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        ) : (
                          <button
                            onClick={() => startBudgetEdit(ln)}
                            className="font-mono text-[#F59E0B] hover:text-amber-200 inline-flex items-center gap-1"
                            title={
                              ln.override_amount != null
                                ? `Pinned override. Source doc amount: ${fmtMoney(ln.source_amount, ccy)}. Click to edit.`
                                : "Click to pin a project-specific budget override"
                            }
                            data-testid={`proj-pline-budget-btn-${idx}`}
                          >
                            {fmtMoney(ln.effective_amount ?? ln.source_amount ?? 0, ccy)}
                            {ln.override_amount != null && (
                              <span className="text-[8px] text-amber-300 ml-0.5">·pin</span>
                            )}
                            {ln.source_changed && (
                              <span className="text-[8px] text-rose-300 ml-0.5" title="Source document has changed since this override was pinned.">·stale</span>
                            )}
                            <Edit3 className="w-2.5 h-2.5 opacity-50" />
                          </button>
                        )}
                      </td>
                      <td
                        className="py-1.5 px-1.5 text-right font-mono text-[#A78BFA]"
                        data-testid={`proj-pline-spent-${idx}`}
                        title={
                          ln.bucket_spent && ln.bucket_ceiling
                            ? `Bucket (SKU / desc / inv-id) total spent: ${fmtMoney(ln.bucket_spent, ccy)} of ${fmtMoney(ln.bucket_ceiling, ccy)}. This line's share proportional to its budget.`
                            : "Actual spend routed to this line."
                        }
                      >
                        {fmtMoney(ln.spent ?? 0, ccy)}
                      </td>
                      <td
                        className={`py-1.5 px-1.5 text-right font-mono ${(ln.headroom ?? 0) < 0 ? "text-rose-300" : "text-[#94A3B8]"}`}
                        data-testid={`proj-pline-headroom-${idx}`}
                      >
                        {fmtMoney(ln.headroom ?? 0, ccy)}
                        {(ln.spent_pct != null && ln.spent_pct >= 90) && (
                          <div className="text-[8px] text-amber-300">{ln.spent_pct}%</div>
                        )}
                      </td>
                      <td className={`py-1.5 px-1.5 text-right font-mono ${marginPos ? "text-emerald-300" : "text-rose-300"}`}>
                        {fmtMoney(ln.line_margin, ccy)}
                      </td>
                      <td className={`py-1.5 pl-1.5 text-right font-mono ${marginPos ? "text-emerald-300" : "text-rose-300"}`}>
                        {ln.line_gp_pct != null ? `${ln.line_gp_pct}%` : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-white/[0.08]">
                  <td colSpan={3} className="py-1.5 pr-2 text-[10px] uppercase tracking-wider text-[#a0aabf] font-semibold">Totals</td>
                  <td className="py-1.5 px-1.5 text-right font-mono text-emerald-300">{fmtMoney(totals.revenue, ccy)}</td>
                  <td className="py-1.5 px-1.5"></td>
                  <td className="py-1.5 px-1.5 text-right font-mono text-rose-300">{fmtMoney(totals.cost, ccy)}</td>
                  <td
                    className="py-1.5 px-1.5 text-right font-mono text-[#F59E0B]"
                    data-testid="proj-pline-budget-total"
                    title="Sum of effective_amount across all budget lines."
                  >
                    {fmtMoney(totals.budget_subtotal ?? totals.budget_total ?? 0, ccy)}
                  </td>
                  <td
                    className="py-1.5 px-1.5 text-right font-mono text-[#A78BFA]"
                    data-testid="proj-pline-spent-total"
                    title={`In-scope: ${fmtMoney(totals.in_scope_total ?? 0, ccy)} · Over budget: ${fmtMoney(totals.over_budget_total ?? 0, ccy)} · Additional: ${fmtMoney(totals.additional_expenses_total ?? 0, ccy)}`}
                  >
                    {fmtMoney(totals.grand_total_spent ?? 0, ccy)}
                  </td>
                  <td
                    className={`py-1.5 px-1.5 text-right font-mono ${(totals.remaining_budget ?? 0) < 0 ? "text-rose-300" : "text-[#94A3B8]"}`}
                    data-testid="proj-pline-headroom-total"
                  >
                    {fmtMoney(totals.remaining_budget ?? 0, ccy)}
                  </td>
                  <td className={`py-1.5 px-1.5 text-right font-mono ${(totals.margin ?? 0) >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                    {fmtMoney(totals.margin, ccy)}
                  </td>
                  <td className={`py-1.5 pl-1.5 text-right font-mono ${(totals.margin ?? 0) >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                    {totals.gp_pct != null ? `${totals.gp_pct}%` : "—"}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          {lines.some((l) => l.cost_source === "override") && (
            <div className="mt-2 text-[10px] text-[#647087] flex items-center gap-1">
              <AlertTriangle className="w-2.5 h-2.5 text-amber-300" />
              Overridden lines use a project-specific unit cost. Clear an override by clicking the cost and setting it to the inventory average.
              <button
                onClick={() => {
                  const overridden = lines.find((l) => l.override_id);
                  if (overridden) clearOverride(overridden);
                }}
                className="ml-auto text-[#1FB6C1] hover:text-white"
                data-testid="proj-pline-clear-first-override"
              >
                Clear first override
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// [Iter 424.j] Spent vs budget with SKU dedup.
// Shows procurement/expense spend routed to a budget line by SKU (in-scope)
// vs everything else (out-of-scope / additional). Per-SKU utilisation bar.
function SpentBreakdownCard({ projectId, ccy, onChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Iter 425.m — Single search input filters every sub-table below
  // (in-scope, over-budget, additional) by SKU or description
  // substring — production project has 80+ line items so scrolling
  // through three lists is untenable.
  const [q, setQ] = useState("");
  // Iter 425.n — Manual assignment state.
  const [selected, setSelected] = useState({});           // key = source_kind::source_id
  const [assignOpen, setAssignOpen] = useState(false);
  const [budgetLines, setBudgetLines] = useState([]);     // for the target picker
  const [assignTarget, setAssignTarget] = useState("");   // "docId::lineIdx"
  const [assignQ, setAssignQ] = useState("");             // filter target picker
  const [applySameSku, setApplySameSku] = useState(false);
  const [assignSaving, setAssignSaving] = useState(false);
  // Iter 425.n.1 — Assignments list for the recap panel.
  const [assignments, setAssignments] = useState([]);
  const [recapOpen, setRecapOpen] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const [{ data: d }, { data: bd }, { data: ax }] = await Promise.all([
        api.get(`/projects/${projectId}/spent-breakdown`),
        api.get(`/projects/${projectId}/budget-breakdown`),
        api.get(`/projects/${projectId}/spend-assignments`),
      ]);
      setData(d);
      setBudgetLines(bd?.lines || []);
      setAssignments(ax || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const rowKey = (it) => `${it.source_kind || ""}::${it.source_id || ""}`;

  // Iter 426.p10 (2026-05-11) — Inline "open source" link on every
  // spend row. Dirk: *"Add an inline link to the original document
  // where it comes from so we can quickly go to the doc and unlink
  // it from the project."* Wrong project allocations are cleared by
  // opening the source and unlinking THIS specific item — the rest
  // of the doc stays as-is.
  const sourceLink = (row) => {
    const kind = (row?.source_kind || "").toLowerCase();
    if (kind === "procurement") {
      // The row carries tracker_id from the backend. Fall back to
      // /procurement if it isn't set (older rows) so the user still
      // lands on the tracker list.
      const tid = row.tracker_id;
      return {
        to: tid ? `/procurement/${tid}?highlight=${row.source_id || ""}` : "/procurement",
        label: `Open procurement tracker${row.source_number ? ` · ${row.source_number}` : ""}`,
      };
    }
    if (kind === "picking") {
      // Iter 427.p36 — source_number was previously the picking-list
      // id; since Iter 427.p3 it holds the human-readable list TITLE,
      // which produced a 404 when passed to /picking/{id}. Use list_id
      // (backend now stamps it on every picking spend row) with a
      // conservative fallback for legacy rows whose source_number
      // still looks like an id.
      const lid = row.list_id || (row.source_number && /^[a-f0-9-]{16,}$/i.test(row.source_number) ? row.source_number : null);
      const label = `Open picking slip${row.source_number ? ` · ${row.source_number}` : ""}`;
      return {
        to: lid ? `/picking/lists/${lid}?highlight=${row.source_id || ""}` : null,
        label,
      };
    }
    if (kind === "purchase_order" || kind === "po") {
      return {
        to: row.source_id ? `/sales-documents/${row.source_id}/edit` : null,
        label: "Open purchase order",
      };
    }
    if (kind === "expense_draft" || kind === "expense") {
      return { to: "/expenses", label: "Open expense drafts" };
    }
    return { to: null, label: null };
  };
  const toggleRow = (it) => {
    const k = rowKey(it);
    setSelected((s) => {
      const n = { ...s };
      if (n[k]) delete n[k];
      else n[k] = it;
      return n;
    });
  };
  const selectedCount = Object.keys(selected).length;
  const openAssign = () => {
    if (selectedCount === 0) return;
    setAssignTarget("");
    setAssignQ("");
    setApplySameSku(false);
    setAssignOpen(true);
  };
  const submitAssign = async () => {
    if (!assignTarget || selectedCount === 0) return;
    const [docId, lineIdxStr] = assignTarget.split("::");
    const lineIndex = parseInt(lineIdxStr, 10);
    setAssignSaving(true);
    try {
      const rows = Object.values(selected)
        .filter((it) => it.source_id)
        .map((it) => ({
          source_kind: it.source_kind,
          source_id: it.source_id,
          sku: it.sku,
        }));
      if (rows.length === 1 && applySameSku && rows[0].sku) {
        // Single-row assign with a "learn" rule.
        await api.post(`/projects/${projectId}/spend-assignments`, {
          source_kind: rows[0].source_kind,
          source_id: rows[0].source_id,
          target_doc_id: docId,
          target_line_index: lineIndex,
          apply_to_same_sku: true,
          sku: rows[0].sku,
        });
      } else {
        await api.post(`/projects/${projectId}/spend-assignments/bulk`, {
          target_doc_id: docId,
          target_line_index: lineIndex,
          rows,
        });
      }
      toast.success(`Assigned ${rows.length} row${rows.length === 1 ? "" : "s"} to placeholder budget line.`);
      setSelected({});
      setAssignOpen(false);
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to assign.");
    } finally {
      setAssignSaving(false);
    }
  };
  // Iter 425.n.1 — One-tap assign from a backend-suggested target.
  const applySuggestion = async (row) => {
    const s = row?.suggested_target;
    if (!s || !row.source_id) return;
    setAssignSaving(true);
    try {
      await api.post(`/projects/${projectId}/spend-assignments`, {
        source_kind: row.source_kind,
        source_id: row.source_id,
        target_doc_id: s.doc_id,
        target_line_index: s.line_index,
      });
      toast.success(`Assigned to ${s.sku || "budget line"}.`);
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to assign.");
    } finally {
      setAssignSaving(false);
    }
  };
  const unassign = async (aid) => {
    if (!window.confirm("Remove this assignment? The spend row returns to Additional.")) return;
    try {
      await api.delete(`/projects/${projectId}/spend-assignments/${aid}`);
      toast.success("Assignment removed.");
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to unassign.");
    }
  };

  if (loading) return <div className="hv-card p-4 text-xs text-[#647087]">Loading spent…</div>;
  if (!data) return null;

  const { budget_total, in_scope_total, out_of_scope_total, remaining_budget,
          in_scope_lines, out_of_scope_items, sku_utilisation,
          over_budget_items = [], additional_expenses = [],
          over_budget_total = 0, additional_expenses_total = 0 } = data;
  const burnPct = budget_total > 0 ? Math.round(100 * in_scope_total / budget_total) : 0;

  const needle = q.trim().toLowerCase();
  const _match = (r) => {
    if (!needle) return true;
    const bits = [r.sku, r.description, r.source_number, r.supplier_name]
      .map((x) => (x || "").toString().toLowerCase());
    return bits.some((b) => b.includes(needle));
  };
  const fSkuUtil = (sku_utilisation || []).filter(_match);
  const fOverBudget = over_budget_items.filter(_match);
  const fAdditional = additional_expenses.filter(_match);

  return (
    <div className="hv-card p-4" data-testid="proj-spent-breakdown-card">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] font-bold flex items-center gap-1.5">
            <Truck className="w-3 h-3" /> Spent vs budget · {fmtMoney(in_scope_total, ccy)} in-scope · {fmtMoney(out_of_scope_total, ccy)} additional
          </div>
          <div className="text-[10px] text-[#647087] mt-0.5">POs, procurement trackers &amp; expenses. SKU match = burns budget; no match = Additional Expenses.</div>
        </div>
        <span className={`text-[10px] px-1.5 py-0.5 rounded ${burnPct >= 100 ? "bg-rose-500/15 text-rose-300" : burnPct >= 80 ? "bg-amber-500/15 text-amber-300" : "bg-emerald-500/15 text-emerald-300"}`} data-testid="proj-spent-burn-pct">
          {burnPct}% burn
        </span>
      </div>
      {/* Headline stats */}
      <div className="grid grid-cols-4 gap-2 mt-2 text-[11px]">
        <div className="border border-white/5 rounded p-2 bg-white/[0.02]">
          <div className="text-[9px] uppercase text-[#647087]">Budget</div>
          <div className="font-semibold text-white">{fmtMoney(budget_total, ccy)}</div>
        </div>
        <div className="border border-emerald-500/20 rounded p-2 bg-emerald-500/[0.04]">
          <div className="text-[9px] uppercase text-[#647087]">In-scope spent</div>
          <div className="font-semibold text-emerald-300" data-testid="proj-spent-in-scope-total">{fmtMoney(in_scope_total, ccy)}</div>
        </div>
        <div className="border border-amber-500/20 rounded p-2 bg-amber-500/[0.04]">
          <div className="text-[9px] uppercase text-[#647087]">Additional</div>
          <div className="font-semibold text-amber-300" data-testid="proj-spent-out-of-scope-total">{fmtMoney(out_of_scope_total, ccy)}</div>
        </div>
        <div className="border border-cyan-500/20 rounded p-2 bg-cyan-500/[0.04]">
          <div className="text-[9px] uppercase text-[#647087]">Remaining</div>
          <div className="font-semibold text-cyan-300" data-testid="proj-spent-remaining">{fmtMoney(remaining_budget, ccy)}</div>
        </div>
      </div>
      {/* Iter 425.n.3 — Global bulk-selection bar. Any selected row
          from in-scope drill-downs, over-budget, or additional feeds
          the same Assign modal. Lets you re-route valid-SKU spend
          away from its default bucket into a placeholder (FIT001) to
          eliminate double-counted budget. */}
      {selectedCount > 0 && (
        <div className="mt-2 flex items-center gap-2 px-2 py-1.5 rounded bg-cyan-500/10 border border-cyan-500/20" data-testid="proj-spent-bulk-bar">
          <span className="text-[10px] text-cyan-300">{selectedCount} selected</span>
          <button
            type="button"
            onClick={openAssign}
            className="hv-btn hv-btn-primary text-[10px] inline-flex items-center gap-1"
            data-testid="proj-spent-assign-btn"
          >
            <Tag className="w-3 h-3" /> Assign to budget line…
          </button>
          <button
            type="button"
            onClick={() => setSelected({})}
            className="hv-btn hv-btn-ghost text-[10px]"
          >
            Clear
          </button>
        </div>
      )}
      {/* Iter 425.n.1 — Assignment recap. Small chip above the SKU
          utilisation bars showing how many rows have been manually
          routed on this project, with a click-through to audit them
          before invoicing. */}
      {assignments.length > 0 && (
        <div className="mt-2 inline-flex items-center gap-2 px-2 py-1 rounded bg-cyan-500/10 border border-cyan-500/20 text-[10px]" data-testid="proj-assign-recap">
          <Tag className="w-3 h-3 text-cyan-300" />
          <span className="text-cyan-300">{assignments.length} row{assignments.length === 1 ? "" : "s"} assigned to budget lines</span>
          <button
            type="button"
            onClick={() => setRecapOpen(true)}
            className="text-cyan-300 hover:text-white underline underline-offset-2"
            data-testid="proj-assign-recap-open"
          >
            View all
          </button>
        </div>
      )}
      {/* Iter 425.m — Legend so the bar colour semantics are unambiguous.
          "Additional" spend lives in its own drilldown below and does
          NOT appear as a coloured bar in the per-SKU list. */}
      <div className="flex flex-wrap items-center gap-3 mt-2 text-[10px] text-[#647087]" data-testid="proj-spent-legend">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" /> In scope
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-rose-500" /> Over budget
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-amber-500" /> Additional · listed below (no bar)
        </span>
      </div>
      {/* Iter 425.m — Search input filters every sub-table below. */}
      <div className="relative mt-3 mb-1" data-testid="proj-spent-search-wrap">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-[#647087]" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search SKU or description…"
          className="hv-input !py-1 !pl-6 !pr-2 text-[11px] w-full max-w-xs"
          data-testid="proj-spent-search"
        />
      </div>
      {/* Per-SKU utilisation */}
      {fSkuUtil.length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="proj-sku-utilisation">
          {fSkuUtil.map((s) => {
            const pct = Math.min(100, s.utilisation_pct);
            const over = s.utilisation_pct > 100;
            // Iter 425.n.3 — In-scope rows that contribute to THIS
            // bucket. Grouped by SKU (empty) or description key so
            // users can drill into a bucket, tick specific spend
            // rows, and re-route them (e.g. move PVC-90-ELBOW into
            // FIT001 instead of its own SKU bucket).
            const bucketRows = (in_scope_lines || []).filter((r) => {
              if (r._pool) return false;
              if (s.sku && r.sku && s.sku.toUpperCase() === (r.sku || "").toUpperCase()) return true;
              if (!s.sku && s.description && r.description
                  && r.description.trim().toLowerCase() === s.description.trim().toLowerCase()) return true;
              return false;
            });
            return (
              <details
                key={s.sku || s.description || Math.random()}
                className="text-[11px]"
                data-testid={`proj-sku-util-${s.sku || s.description}`}
              >
                <summary className="cursor-pointer list-none">
                  <div className="flex justify-between items-baseline">
                    <span className="text-[#cbd5e1] inline-flex items-center gap-1">
                      <ChevronRight className="w-3 h-3 text-[#647087] transition-transform group-open:rotate-90" />
                      {s.sku
                        ? <span className="font-mono text-[10px] text-[#647087] mr-2">{s.sku}</span>
                        : null}
                      {s.description || (s.sku ? "" : "— unnamed —")}
                    </span>
                    <span className="text-[10px] text-[#647087]">
                      {fmtMoney(s.spent, ccy)} / {fmtMoney(s.ceiling, ccy)}
                      {bucketRows.length > 0 && (
                        <span className="ml-2 text-[#647087]">· {bucketRows.length} row{bucketRows.length === 1 ? "" : "s"}</span>
                      )}
                    </span>
                  </div>
                  <div className="mt-0.5 h-1.5 rounded bg-white/[0.06] overflow-hidden">
                    <div className={`h-full ${over ? "bg-rose-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
                  </div>
                </summary>
                {bucketRows.length > 0 && (
                  <div className="ml-4 mt-1 mb-2 border-l border-white/[0.06] pl-2 space-y-0.5">
                    {bucketRows.map((r, ri) => {
                      const k = rowKey(r);
                      const selectable = Boolean(r.source_id && r.source_kind);
                      const subs = Array.isArray(r.sub_items) ? r.sub_items : [];
                      return (
                        <div key={ri} className="text-[10px] py-0.5">
                          <div className="flex items-center justify-between">
                            <label className="inline-flex items-center gap-1.5 cursor-pointer flex-1 min-w-0">
                              {selectable && (
                                <input
                                  type="checkbox"
                                  checked={!!selected[k]}
                                  onChange={() => toggleRow({ ...r, _pool: "inscope" })}
                                  className="cursor-pointer"
                                  data-testid={`proj-inscope-select-${k}`}
                                />
                              )}
                              <span className="text-[#a0aabf] truncate">
                                {r.source_kind}{r.source_number ? ` · ${r.source_number}` : ""}
                                {(r.sku || r.description) && (
                                  <span className="text-[#647087]">
                                    {" — "}
                                    {r.sku ? <span className="font-mono">{r.sku}</span> : ""}
                                    {r.sku && r.description ? " · " : ""}
                                    {r.description || ""}
                                  </span>
                                )}
                                {subs.length > 0 && (
                                  <span className="ml-1.5 text-[9px] text-[#647087] font-mono">
                                    · {subs.length} item{subs.length === 1 ? "" : "s"}
                                  </span>
                                )}
                              </span>
                            </label>
                            <div className="inline-flex items-center gap-2 shrink-0">
                              {sourceLink(r).to && (
                                <Link
                                  to={sourceLink(r).to}
                                  onClick={(ev) => ev.stopPropagation()}
                                  className="p-0.5 rounded text-cyan-300 hover:bg-cyan-500/15"
                                  title={sourceLink(r).label}
                                  data-testid={`proj-inscope-open-${k}`}
                                >
                                  <ExternalLink className="w-3 h-3" />
                                </Link>
                              )}
                              <span className="text-emerald-300 tabular-nums">{fmtMoney(r.amount, ccy)}</span>
                            </div>
                          </div>
                          {/* Iter 427.p36 — sub-item breakdown for picking rows so
                              ops can see every SKU picked under this bucket and
                              spot mis-assignments without opening the slip. */}
                          {subs.length > 0 && (
                            <div className="ml-5 mt-0.5 pl-2 border-l border-white/[0.04] space-y-0.5" data-testid={`proj-inscope-sub-${k}`}>
                              {subs.map((si, si_i) => (
                                <div key={si_i} className="flex items-center justify-between text-[10px] text-[#7c8699]">
                                  <span className="truncate">
                                    {si.sku ? <span className="font-mono text-[#a0aabf]">{si.sku}</span> : null}
                                    {si.sku && si.name ? " · " : ""}
                                    <span>{si.name || (si.sku ? "" : "— unnamed —")}</span>
                                    <span className="ml-1 text-[#647087]">× {Number(si.qty_picked || 0).toLocaleString()}</span>
                                    {si.unit_rate ? (
                                      <span className="ml-1 text-[#647087]">@ {fmtMoney(si.unit_rate, ccy)}</span>
                                    ) : null}
                                  </span>
                                  <span className="tabular-nums text-emerald-200/70 shrink-0 ml-2">{fmtMoney(si.line_amount, ccy)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </details>
            );
          })}
        </div>
      )}
      {/* Iter 425.l — Split into two clearly-named drilldowns. */}
      {fOverBudget.length > 0 && (
        <details className="mt-3" data-testid="proj-over-budget" open={Object.values(selected).some((s) => s?._pool === "over") ? true : undefined}>
          <summary className="text-[10px] uppercase tracking-wider text-rose-300 cursor-pointer">
            Over budget · {fOverBudget.length} line{fOverBudget.length === 1 ? "" : "s"} · {fmtMoney(over_budget_total, ccy)}
            {needle && <span className="ml-2 text-[#647087]">(filtered)</span>}
          </summary>
          <div className="text-[10px] text-[#647087] mt-0.5">Spend that matched a SKU/description but exceeded the bucket ceiling. Select + Assign to route it to a different placeholder line (e.g. FIT001) and eliminate double-counted budget.</div>
          <table className="w-full text-[11px] mt-1">
            <thead>
              <tr className="text-[10px] text-[#647087] border-b border-white/5">
                <th className="w-6 py-1 px-1.5"></th>
                <th className="text-left py-1 px-1.5">Source</th>
                <th className="text-left py-1 px-1.5">Budget line</th>
                <th className="text-right py-1 px-1.5">Over</th>
              </tr>
            </thead>
            <tbody>
              {fOverBudget.map((it, idx) => {
                const k = rowKey(it);
                const selectable = Boolean(it.source_id && it.source_kind);
                return (
                  <tr key={idx} className="border-b border-white/[0.03]">
                    <td className="py-1 px-1.5 text-center">
                      {selectable && (
                        <input
                          type="checkbox"
                          checked={!!selected[k]}
                          onChange={() => toggleRow({ ...it, _pool: "over" })}
                          className="cursor-pointer"
                          data-testid={`proj-overbudget-select-${idx}`}
                        />
                      )}
                    </td>
                    <td className="py-1 px-1.5 text-[#a0aabf]">
                      <div className="inline-flex items-center gap-1.5">
                        <span>{it.source_kind}{it.source_number ? ` · ${it.source_number}` : ""}</span>
                        {sourceLink(it).to && (
                          <Link
                            to={sourceLink(it).to}
                            className="p-0.5 rounded text-cyan-300 hover:bg-cyan-500/15"
                            title={sourceLink(it).label}
                            data-testid={`proj-overbudget-open-${idx}`}
                          >
                            <ExternalLink className="w-3 h-3" />
                          </Link>
                        )}
                      </div>
                    </td>
                    <td className="py-1 px-1.5 text-[10px] text-[#a0aabf]">
                      {it.sku ? <span className="font-mono">{it.sku}</span> : null}
                      {it.sku && it.description ? " · " : ""}
                      {it.description || ""}
                    </td>
                    <td className="py-1 px-1.5 text-right text-rose-300">{fmtMoney(it.amount, ccy)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </details>
      )}
      {fAdditional.length > 0 && (
        <details className="mt-3" data-testid="proj-additional-expenses" open={selectedCount > 0 ? true : undefined}>
          <summary className="text-[10px] uppercase tracking-wider text-amber-300 cursor-pointer">
            Additional expenses · {fAdditional.length} item{fAdditional.length === 1 ? "" : "s"} · {fmtMoney(additional_expenses_total, ccy)}
            {needle && <span className="ml-2 text-[#647087]">(filtered)</span>}
          </summary>
          <div className="text-[10px] text-[#647087] mt-0.5">Spend with no matching SKU/description on any budget line. Select rows and assign to a placeholder line (e.g. FIT001, ELEC001) to burn its budget instead.</div>
          <table className="w-full text-[11px] mt-1">
            <thead>
              <tr className="text-[10px] text-[#647087] border-b border-white/5">
                <th className="w-6 py-1 px-1.5"></th>
                <th className="text-left py-1 px-1.5">Source</th>
                <th className="text-left py-1 px-1.5">Reason</th>
                <th className="text-right py-1 px-1.5">Amount</th>
              </tr>
            </thead>
            <tbody>
              {fAdditional.map((it, idx) => {
                const k = rowKey(it);
                const selectable = Boolean(it.source_id && it.source_kind);
                return (
                  <tr key={idx} className="border-b border-white/[0.03]">
                    <td className="py-1 px-1.5 text-center">
                      {selectable && (
                        <input
                          type="checkbox"
                          checked={!!selected[k]}
                          onChange={() => toggleRow({ ...it, _pool: "additional" })}
                          className="cursor-pointer"
                          data-testid={`proj-additional-select-${idx}`}
                        />
                      )}
                    </td>
                    <td className="py-1 px-1.5 text-[#a0aabf]">
                      <div className="inline-flex items-center gap-1.5">
                        <span>{it.source_kind}{it.source_number ? ` · ${it.source_number}` : ""}</span>
                        {sourceLink(it).to && (
                          <Link
                            to={sourceLink(it).to}
                            className="p-0.5 rounded text-cyan-300 hover:bg-cyan-500/15"
                            title={sourceLink(it).label}
                            data-testid={`proj-additional-open-${idx}`}
                          >
                            <ExternalLink className="w-3 h-3" />
                          </Link>
                        )}
                      </div>
                      {(it.sku || it.description) && (
                        <div className="text-[9px] text-[#647087]">
                          {it.sku ? `SKU ${it.sku}` : ""}
                          {it.sku && it.description ? " · " : ""}
                          {it.description || ""}
                        </div>
                      )}
                    </td>
                    <td className="py-1 px-1.5 text-[10px] text-amber-300">
                      {it.reason === "no_key"
                        ? "no SKU or description"
                        : it.reason === "not_in_budget"
                          ? "not in budget"
                          : it.reason === "no_sku"
                            ? "no SKU on item"
                            : it.reason === "sku_not_in_budget"
                              ? "SKU not in budget"
                              : "unmatched"}
                      {it.suggested_target && selectable && (
                        <button
                          type="button"
                          onClick={() => applySuggestion(it)}
                          disabled={assignSaving}
                          className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10 text-[9px] transition"
                          title={`Suggested match (${it.suggested_target.confidence}%): ${it.suggested_target.description || it.suggested_target.sku}. Click to assign.`}
                          data-testid={`proj-additional-suggest-${idx}`}
                        >
                          <Tag className="w-2.5 h-2.5" />
                          Suggest {it.suggested_target.sku || "→"}
                          <span className="text-[8px] opacity-70">{it.suggested_target.confidence}%</span>
                        </button>
                      )}
                    </td>
                    <td className="py-1 px-1.5 text-right text-amber-300">{fmtMoney(it.amount, ccy)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </details>
      )}
      {/* Iter 425.n.1 — Assignment recap modal. Lists all manual
          spend-assignments on this project with quick unassign. */}
      {recapOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
          onClick={() => setRecapOpen(false)}
          data-testid="proj-assign-recap-modal"
        >
          <div
            className="hv-card p-4 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-white">
                Spend assignments · {assignments.length}
              </div>
              <button onClick={() => setRecapOpen(false)} className="text-[#647087] hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="text-[10px] text-[#647087] mb-2">
              Each row here has been manually routed to a placeholder budget line. Unassign to return the row to Additional expenses.
            </div>
            {assignments.length === 0 ? (
              <div className="text-[11px] text-[#647087] italic p-3">No assignments yet.</div>
            ) : (
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-[10px] text-[#647087] border-b border-white/5">
                    <th className="text-left py-1 px-1.5">Source</th>
                    <th className="text-left py-1 px-1.5">Assigned to</th>
                    <th className="text-left py-1 px-1.5">By / when</th>
                    <th className="text-right py-1 px-1.5 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {assignments.map((a) => (
                    <tr key={a.id} className="border-b border-white/[0.03]" data-testid={`proj-assign-recap-row-${a.id}`}>
                      <td className="py-1 px-1.5 text-[#a0aabf]">
                        {a.source_kind ? (
                          <div>
                            <div>{a.source_kind} · <code className="text-[10px]">{(a.source_id || "").slice(0, 12)}</code></div>
                          </div>
                        ) : (
                          <div>
                            <span className="inline-block px-1 py-px rounded bg-cyan-500/15 text-cyan-300 text-[9px] mr-1">RULE</span>
                            SKU <code className="text-[10px]">{a.apply_sku}</code>
                          </div>
                        )}
                      </td>
                      <td className="py-1 px-1.5 text-[#cbd5e1]">
                        <div className="font-mono text-[10px] text-[#647087]">{a.target_sku || "—"}</div>
                        <div>{a.target_description}</div>
                      </td>
                      <td className="py-1 px-1.5 text-[10px] text-[#647087]">
                        {a.assigned_by || "—"}
                        {a.assigned_at && (
                          <div>{new Date(a.assigned_at).toLocaleString()}</div>
                        )}
                      </td>
                      <td className="py-1 px-1.5 text-right">
                        <button
                          onClick={() => unassign(a.id)}
                          className="text-rose-400 hover:text-rose-300"
                          data-testid={`proj-assign-recap-unassign-${a.id}`}
                          title="Unassign"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
      {/* Iter 425.n — Assign-to-budget-line modal. */}
      {assignOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
          onClick={() => !assignSaving && setAssignOpen(false)}
          data-testid="proj-assign-modal"
        >
          <div
            className="hv-card p-4 w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-white">
                Assign {selectedCount} row{selectedCount === 1 ? "" : "s"} to budget line
              </div>
              <button
                onClick={() => setAssignOpen(false)}
                className="text-[#647087] hover:text-white"
                disabled={assignSaving}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="text-[10px] text-[#647087] mb-2">
              Pick a placeholder line on this project (e.g. FIT001, ELEC001, QTE). The selected spend rows will burn its budget instead of appearing in Additional.
            </div>
            <div className="relative mb-2">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-[#647087]" />
              <input
                type="text"
                value={assignQ}
                onChange={(e) => setAssignQ(e.target.value)}
                placeholder="Search SKU or description…"
                className="hv-input !py-1 !pl-6 !pr-2 text-[11px] w-full"
                autoFocus
                data-testid="proj-assign-search"
              />
            </div>
            <div className="border border-white/5 rounded max-h-64 overflow-y-auto">
              {budgetLines
                .filter((bl) => {
                  const n = assignQ.trim().toLowerCase();
                  if (!n) return true;
                  return [bl.sku, bl.description, bl.doc_number]
                    .some((x) => (x || "").toString().toLowerCase().includes(n));
                })
                .map((bl) => {
                  const key = `${bl.doc_id}::${bl.line_index}`;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setAssignTarget(key)}
                      className={`w-full text-left px-3 py-1.5 border-b border-white/[0.03] text-[11px] transition ${
                        assignTarget === key
                          ? "bg-cyan-500/15 text-white"
                          : "text-[#a0aabf] hover:bg-white/[0.03]"
                      }`}
                      data-testid={`proj-assign-target-${key}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <span className="font-mono text-[10px] text-[#647087] mr-2">{bl.sku || "—"}</span>
                          {bl.description}
                        </div>
                        <div className="text-[10px] text-[#F59E0B] font-mono">{fmtMoney(bl.effective_amount, ccy)}</div>
                      </div>
                      <div className="text-[9px] text-[#647087] mt-0.5">{bl.doc_kind}{bl.doc_number ? ` · ${bl.doc_number}` : ""}</div>
                    </button>
                  );
                })}
              {budgetLines.length === 0 && (
                <div className="p-3 text-[10px] text-[#647087] italic">No budget lines on this project.</div>
              )}
            </div>
            {selectedCount === 1 && (
              <label className="flex items-center gap-2 mt-2 text-[10px] text-[#a0aabf] cursor-pointer" data-testid="proj-assign-learn">
                <input
                  type="checkbox"
                  checked={applySameSku}
                  onChange={(e) => setApplySameSku(e.target.checked)}
                  disabled={!Object.values(selected)[0]?.sku}
                />
                Also apply to future rows with same SKU on this project
              </label>
            )}
            <div className="flex items-center justify-end gap-2 mt-3">
              <button
                onClick={() => setAssignOpen(false)}
                className="hv-btn hv-btn-ghost text-[11px]"
                disabled={assignSaving}
              >
                Cancel
              </button>
              <button
                onClick={submitAssign}
                disabled={!assignTarget || assignSaving}
                className="hv-btn hv-btn-primary text-[11px] inline-flex items-center gap-1"
                data-testid="proj-assign-submit"
              >
                {assignSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                Assign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}





// [Iter 420] Manual project budget items. Users add lines that count
// toward the project's budget without going through a quote (e.g.
// contingency, out-of-scope buffer, provisional travel). Adds to the
// effective_budget on the backend automatically.
function BudgetItemsCard({ projectId, ccy, onChange }) {
  const [rows, setRows] = useState([]);
  const [subtotal, setSubtotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [desc, setDesc] = useState("");
  const [qty, setQty] = useState("1");
  const [cost, setCost] = useState("");
  const [saving, setSaving] = useState(false);
  // Iter 425.n.2 — Tenant placeholder presets (FIT001, ELEC001, …).
  const [presets, setPresets] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, { data: pp }] = await Promise.all([
        api.get(`/projects/${projectId}/budget-items`),
        api.get(`/placeholder-presets`).catch(() => ({ data: [] })),
      ]);
      setRows(data?.items || []);
      setSubtotal(data?.subtotal || 0);
      setPresets(Array.isArray(pp) ? pp : []);
    } catch {} finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const q = parseFloat(qty), u = parseFloat(cost);
    if (!desc.trim()) { toast.error("Description required."); return; }
    if (!Number.isFinite(q) || q < 0) { toast.error("Quantity must be ≥ 0."); return; }
    if (!Number.isFinite(u) || u < 0) { toast.error("Unit cost must be ≥ 0."); return; }
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/budget-items`, {
        description: desc.trim(), quantity: q, unit_cost: u,
      });
      toast.success("Budget line added.");
      setDesc(""); setQty("1"); setCost(""); setAdding(false);
      await load();
      onChange && onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add.");
    } finally { setSaving(false); }
  };

  const remove = async (iid) => {
    if (!window.confirm("Remove this budget line?")) return;
    try {
      await api.delete(`/projects/${projectId}/budget-items/${iid}`);
      toast.success("Removed.");
      await load();
      onChange && onChange();
    } catch (err) {
      toast.error("Failed to remove.");
    }
  };

  // Iter 425.n.2 — One-tap add a preset line to this project's budget.
  const addPreset = async (p) => {
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/budget-items`, {
        description: `${p.sku} · ${p.description}`,
        quantity: 1,
        unit_cost: p.default_budget || 0,
      });
      toast.success(`${p.sku} added to project budget.`);
      await load();
      onChange && onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add preset.");
    } finally { setSaving(false); }
  };

  return (
    <div className="hv-card !p-3" data-testid="proj-budget-items-card">
      <div className="flex items-center justify-between mb-2">
        <div className="font-display font-semibold text-sm text-[#F59E0B] inline-flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5" /> Manual budget lines
          <span className="text-[10px] text-[#647087] font-mono">· {rows.length} · {fmtMoney(subtotal, ccy)}</span>
        </div>
        <button
          type="button"
          onClick={() => setAdding((v) => !v)}
          className={`hv-btn text-[11px] ${adding ? "hv-btn-primary" : "hv-btn-ghost"}`}
          data-testid="proj-budget-add-toggle"
        >
          <Plus className="w-3 h-3" /> {adding ? "Cancel" : "Add line"}
        </button>
      </div>
      {/* Iter 425.n.2 — Preset chips. One-tap add a placeholder
          category line (FIT001, ELEC001, TRAV, GENERAL) with the
          tenant default budget. Managed at /settings/projects. */}
      {presets.length > 0 && (
        <div className="mb-2 flex items-center gap-1 flex-wrap" data-testid="proj-budget-presets">
          <span className="text-[9px] uppercase tracking-wider text-[#647087]">Presets:</span>
          {presets.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => addPreset(p)}
              disabled={saving}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10 text-[10px] transition disabled:opacity-50"
              title={`${p.description}${p.default_budget ? ` · default ${fmtMoney(p.default_budget, ccy)}` : ""}`}
              data-testid={`proj-budget-preset-${p.sku}`}
            >
              <Plus className="w-2.5 h-2.5" /> {p.sku}
            </button>
          ))}
        </div>
      )}
      {adding && (
        <form onSubmit={submit} className="mb-2 p-2 rounded border border-[#F59E0B]/30 bg-white/[0.02] flex flex-wrap items-end gap-2" data-testid="proj-budget-add-form">
          <label className="flex-1 min-w-[140px] text-[10px] text-[#647087] uppercase tracking-wider">
            Description
            <input value={desc} onChange={(e) => setDesc(e.target.value)} className="hv-input mt-0.5 w-full text-[11px]" placeholder="Contingency / travel buffer" data-testid="proj-budget-desc" required />
          </label>
          <label className="w-16 text-[10px] text-[#647087] uppercase tracking-wider">
            Qty
            <input type="number" step="0.01" min="0" value={qty} onChange={(e) => setQty(e.target.value)} className="hv-input mt-0.5 w-full text-[11px] font-mono text-right" data-testid="proj-budget-qty" />
          </label>
          <label className="w-24 text-[10px] text-[#647087] uppercase tracking-wider">
            Unit cost
            <input type="number" step="0.01" min="0" value={cost} onChange={(e) => setCost(e.target.value)} className="hv-input mt-0.5 w-full text-[11px] font-mono text-right" data-testid="proj-budget-cost" required />
          </label>
          <button type="submit" disabled={saving} className="hv-btn hv-btn-primary text-[11px]" data-testid="proj-budget-save">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            Save
          </button>
        </form>
      )}
      {loading ? (
        <div className="text-[11px] text-[#647087] italic">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-[11px] text-[#647087] italic">
          No manual budget lines. Add one to include contingency, travel, or provisional cost buffers in the budget.
        </div>
      ) : (
        <table className="w-full text-[11px]" data-testid="proj-budget-items-table">
          <tbody>
            {rows.map((it) => (
              <tr key={it.id} className="border-b border-white/[0.03]" data-testid={`proj-budget-row-${it.id}`}>
                <td className="py-1 pr-2 text-white truncate">{it.description}</td>
                <td className="py-1 px-1.5 text-right font-mono text-[10px] text-[#a0aabf]">{it.quantity}</td>
                <td className="py-1 px-1.5 text-right font-mono text-[10px] text-[#a0aabf]">{fmtMoney(it.unit_cost, ccy)}</td>
                <td className="py-1 px-1.5 text-right font-mono text-[#F59E0B]">{fmtMoney(it.line_total, ccy)}</td>
                <td className="py-1 pl-1">
                  <button onClick={() => remove(it.id)} className="text-[#647087] hover:text-rose-400" data-testid={`proj-budget-del-${it.id}`}>
                    <X className="w-3 h-3" />
                  </button>
                </td>
              </tr>
            ))}
            <tr className="border-t-2 border-white/[0.08]">
              <td colSpan={3} className="py-1.5 text-[10px] uppercase tracking-wider text-[#a0aabf] font-semibold">Subtotal</td>
              <td className="py-1.5 px-1.5 text-right font-mono text-[#F59E0B] font-semibold">{fmtMoney(subtotal, ccy)}</td>
              <td />
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}

// [Iter 420] Manual expense entry (no OCR). Users can type in supplier,
// date, total and optional tax/notes directly instead of shooting the
// camera. Lands in status="pending" so the review queue works normally.
function ManualExpenseForm({ projectId, onCancel, onCreated }) {
  const [supplier, setSupplier] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [total, setTotal] = useState("");
  const [tax, setTax] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    const totalV = parseFloat(total);
    if (!Number.isFinite(totalV) || totalV < 0) {
      toast.error("Enter a valid total (≥ 0).");
      return;
    }
    setSaving(true);
    try {
      await api.post("/expenses/manual", {
        project_id: projectId,
        supplier_name: supplier.trim() || null,
        date: date || null,
        total: totalV,
        tax: tax ? parseFloat(tax) : null,
        subtotal: tax ? totalV - parseFloat(tax) : null,
        notes: notes.trim() || null,
      });
      toast.success("Manual expense captured.");
      onCreated && onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create expense.");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="mb-2 p-2 rounded border border-[#1FB6C1]/30 bg-white/[0.02] space-y-2" data-testid="proj-manual-expense-form">
      <div className="grid grid-cols-2 gap-2">
        <label className="text-[10px] text-[#647087] uppercase tracking-wider">
          Supplier
          <input
            value={supplier}
            onChange={(e) => setSupplier(e.target.value)}
            className="hv-input mt-0.5 w-full text-[11px]"
            placeholder="e.g. Shell garage"
            data-testid="proj-manual-supplier"
          />
        </label>
        <label className="text-[10px] text-[#647087] uppercase tracking-wider">
          Date
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="hv-input mt-0.5 w-full text-[11px]"
            data-testid="proj-manual-date"
          />
        </label>
        <label className="text-[10px] text-[#647087] uppercase tracking-wider">
          Total (incl. VAT)
          <input
            type="number"
            step="0.01"
            min="0"
            value={total}
            onChange={(e) => setTotal(e.target.value)}
            className="hv-input mt-0.5 w-full text-[11px] font-mono"
            required
            data-testid="proj-manual-total"
          />
        </label>
        <label className="text-[10px] text-[#647087] uppercase tracking-wider">
          Tax / VAT (optional)
          <input
            type="number"
            step="0.01"
            min="0"
            value={tax}
            onChange={(e) => setTax(e.target.value)}
            className="hv-input mt-0.5 w-full text-[11px] font-mono"
            data-testid="proj-manual-tax"
          />
        </label>
      </div>
      <label className="block text-[10px] text-[#647087] uppercase tracking-wider">
        Notes (optional)
        <DictTextarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="hv-input mt-0.5 w-full text-[11px]"
          placeholder="What was this for?"
          data-testid="proj-manual-notes"
        />
      </label>
      <div className="flex items-center gap-1 justify-end">
        <button type="button" onClick={onCancel} disabled={saving} className="hv-btn hv-btn-ghost text-[11px]" data-testid="proj-manual-cancel">
          Cancel
        </button>
        <button type="submit" disabled={saving || !total} className="hv-btn hv-btn-primary text-[11px]" data-testid="proj-manual-submit">
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
          Save expense
        </button>
      </div>
    </form>
  );
}

function ExpensesCard({ proj, expenses, onReload, isManagerPlus, isTech, canSeeCosts }) {
  const [manualOpen, setManualOpen] = useState(false);
  return (
    <div className="hv-card !p-3" data-testid="proj-expenses-card">
      <div className="flex items-center justify-between mb-2">
        <div className="font-display font-semibold text-sm text-[#a0aabf] inline-flex items-center gap-1.5">
          <ReceiptIcon className="w-3.5 h-3.5" /> Expenses
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setManualOpen((v) => !v)}
            className={`hv-btn text-[11px] ${manualOpen ? "hv-btn-primary" : "hv-btn-ghost"}`}
            data-testid="proj-manual-expense-toggle"
          >
            <Plus className="w-3 h-3" /> {manualOpen ? "Cancel" : "Add manual"}
          </button>
          <Link
            to={`/camera?expense=1&project=${proj.id}`}
            className="hv-btn hv-btn-primary text-[11px]"
            data-testid="proj-capture-receipt"
          >
            <Camera className="w-3 h-3" /> Capture
          </Link>
        </div>
      </div>
      {manualOpen && (
        <ManualExpenseForm
          projectId={proj.id}
          onCancel={() => setManualOpen(false)}
          onCreated={() => { setManualOpen(false); onReload && onReload(); }}
        />
      )}
      {expenses.length === 0 ? (
        <div className="text-[11px] text-[#647087] italic">No expenses captured yet.</div>
      ) : (
        <ul className="space-y-1 max-h-72 overflow-y-auto">
          {expenses.slice(0, 12).map((e) => (
            <li key={e.id} className="text-xs px-1.5 py-1 rounded hover:bg-white/[0.03]" data-testid={`proj-expense-${e.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-white truncate">{e.supplier_name || "Pending OCR…"}</div>
                  <div className="text-[10px] text-[#647087]">
                    {e.captured_by_name} · {e.date || (e.captured_at || "").slice(0, 10)}
                  </div>
                </div>
                {canSeeCosts && e.total != null && (
                  <div className="font-mono text-xs text-white">{fmtMoney(e.total, e.currency || "ZAR")}</div>
                )}
              </div>
              <div className="flex items-center justify-between gap-2 mt-0.5">
                <ExpenseStatusPill status={e.status} confidence={e.confidence} />
                {isManagerPlus && e.status === "pending" && (
                  <Link to="/expenses/review" className="text-[10px] text-[#1FB6C1] underline">Review →</Link>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {expenses.length > 12 && (
        <Link to={`/expenses/review?project=${proj.id}`} className="block text-[11px] text-[#1FB6C1] mt-2 text-center">
          View all {expenses.length} expenses →
        </Link>
      )}
    </div>
  );
}

export function ExpenseStatusPill({ status, confidence }) {
  const map = {
    ocr_pending: { color: "#0EA5E9", label: "OCR running…", icon: Loader2, spin: true },
    pending:     { color: "#F59E0B", label: "Pending review", icon: AlertTriangle },
    approved:    { color: "#10B981", label: "Approved", icon: Check },
    rejected:    { color: "#F43F5E", label: "Rejected", icon: X },
    ocr_failed:  { color: "#F43F5E", label: "OCR failed", icon: AlertTriangle },
  };
  const m = map[status] || { color: "#647087", label: status };
  const Icon = m.icon;
  return (
    <span className="text-[9px] uppercase tracking-wider inline-flex items-center gap-0.5 px-1 py-0.5 rounded-full"
      style={{ background: `${m.color}1a`, color: m.color }}>
      {Icon && <Icon className={`w-2.5 h-2.5 ${m.spin ? "animate-spin" : ""}`} />}
      {m.label}
      {confidence != null && status === "pending" && (
        <span className="text-[#647087] ml-1">{Math.round(confidence * 100)}%</span>
      )}
    </span>
  );
}

function TimelineSection({ timeline }) {
  return (
    <div className="hv-card !p-3" data-testid="proj-timeline-card">
      <div className="font-display font-semibold text-sm text-[#a0aabf] inline-flex items-center gap-1.5 mb-2">
        <Activity className="w-3.5 h-3.5" /> Activity timeline
        <span className="text-[10px] text-[#647087] font-mono">({timeline.length})</span>
      </div>
      {timeline.length === 0 ? (
        <div className="text-[11px] text-[#647087] italic">No activity yet.</div>
      ) : (
        <ul className="space-y-1.5 max-h-96 overflow-y-auto">
          {timeline.map((t, i) => (
            <li key={i} className="flex items-start gap-2 text-xs">
              <div className="text-[9px] font-mono text-[#647087] w-24 shrink-0 pt-0.5">
                {new Date(t.at).toLocaleString(undefined, { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" })}
              </div>
              <div className="flex-1 min-w-0">
                {t.link_url ? (
                  <Link to={t.link_url} className="hover:text-[#1FB6C1] inline-flex items-center gap-1">
                    {t.title}
                    <ExternalLink className="w-2.5 h-2.5 inline" />
                  </Link>
                ) : (
                  <span>{t.title}</span>
                )}
                {t.actor_name && <span className="text-[#647087] ml-1">· {t.actor_name}</span>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function LinkPicker({ kind, customerId, onClose, onPick, onPickMany }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(() => new Set()); // Iter 424.n multi-select

  useEffect(() => {
    const ctl = new AbortController();
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        let endpoint = "/jobcards";
        let params = { search: query, limit: 100 };
        if (kind === "quote" || kind === "sales_order" || kind === "invoice" || kind === "credit_note" || kind === "purchase_order") {
          endpoint = "/sales-documents";
          // Iter 424.n — pass `q` so the server-side filter narrows the
          // list. Bumped limit so users searching in a big dataset get
          // full match coverage.
          // Iter 424.o — purchase_order now uses the same picker path
          // as quotes / SOs / invoices. Customer scope applies too so
          // the operator only sees POs raised for THIS customer.
          params = { category: kind, customer_id: customerId, q: query, limit: 200 };
        } else if (kind === "site") {
          endpoint = "/sites";
          params = { customer_id: customerId, limit: 100 };
        } else if (kind === "ticket") {
          endpoint = "/tickets";
          params = { q: query, limit: 50 };
        } else if (kind === "meeting") {
          endpoint = "/meetings";
          params = { q: query, limit: 50 };
        } else if (kind === "task") {
          endpoint = "/tasks";
          params = { q: query, limit: 50 };
        } else if (kind === "jobcard") {
          endpoint = "/jobcards";
          params = { search: query, customer_id: customerId, limit: 100 };
        } else if (kind === "procurement_tracker") {
          endpoint = "/procurement/trackers";
          params = { limit: 100, q: query };
        } else if (kind === "picking_list") {
          endpoint = "/picking/lists";
          params = { customer_id: customerId, limit: 100, q: query };
        }
        const { data } = await api.get(endpoint, { params, signal: ctl.signal });
        const arr = Array.isArray(data) ? data : (data?.items || []);
        // Client-side final-pass filter — belt & braces for endpoints
        // that ignore `q` (Iter 424.n).
        const q = (query || "").trim().toLowerCase();
        const clientFiltered = q
          ? arr.filter((r) => {
              const hay = [r.document_number, r.title, r.name, r.subject,
                            r.ref, r.short_id, r.supplier_name, r.code]
                .filter(Boolean).join(" ").toLowerCase();
              return hay.includes(q);
            })
          : arr;
        // Un-linked first, already-linked at the bottom.
        const unlinked = clientFiltered.filter((r) => !r.project_id);
        const linked = clientFiltered.filter((r) => r.project_id);
        setResults([...unlinked, ...linked]);
      } catch (e) {
        if (e?.name !== "CanceledError") setResults([]);
      } finally { setSearching(false); }
    }, 200);
    return () => { clearTimeout(t); ctl.abort(); };
  }, [query, kind, customerId]);

  const labelFor = (r) => {
    if (kind.includes("quote") || kind.includes("order") || kind.includes("invoice") || kind === "credit_note") {
      return `${r.document_number || r.title || r.id?.slice(0, 8)} — ${r.title || ""} — ${r.currency || ""} ${r.amount || ""}`;
    }
    if (kind === "site") return r.label || r.name || r.id;
    if (kind === "ticket") return `${r.short_id || r.ref || r.id?.slice(0, 8)} — ${r.subject || r.title || ""}`;
    if (kind === "meeting") return r.title;
    if (kind === "task") return r.title;
    if (kind === "jobcard") return `${r.short_id || r.ref || r.id?.slice(0, 8)} — ${r.title || ""}`;
    if (kind === "procurement_tracker") return `${r.name || r.id?.slice(0, 12)} — ${r.supplier_name || ""}${r.status ? " · " + r.status : ""}`;
    if (kind === "picking_list") return `${r.name || r.code || r.id?.slice(0, 12)}${r.status ? " · " + r.status : ""}`;
    return r.title || r.id;
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-[#0a0b0e] flex flex-col"
      data-testid="proj-link-fullscreen"
    >
      {/* Full-screen header */}
      <div className="border-b border-white/10 px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <button
            type="button"
            onClick={onClose}
            className="hv-btn hv-btn-ghost text-xs shrink-0"
            data-testid="proj-link-close"
            aria-label="Close"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
          </button>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-[#647087]">Link to project</div>
            <h2 className="font-display text-lg sm:text-xl font-bold capitalize truncate">
              Pick a {kind.replace("_", " ")}
            </h2>
          </div>
        </div>
        <button onClick={onClose} className="hv-btn hv-btn-ghost text-xs" aria-label="Close" data-testid="proj-link-close-x">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Search bar + Multi-select toolbar (Iter 424.n) */}
      <div className="border-b border-white/5 px-4 py-3">
        <div className="relative max-w-xl">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#647087]" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${kind.replace("_", " ")}…`}
            className="hv-input text-sm pl-8 pr-8 w-full"
            autoFocus
            data-testid="proj-link-search"
          />
          {searching && <Loader2 className="w-4 h-4 absolute right-2.5 top-1/2 -translate-y-1/2 animate-spin text-[#647087]" />}
        </div>
        <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
          <div className="text-[11px] text-[#647087]">
            {selected.size > 0
              ? `${selected.size} selected · click Link Selected to attach all in one go`
              : "Tick multiple rows to link them at once, or click a single row to link + close."}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSelected(new Set(results.map((r) => r.id)))}
              disabled={results.length === 0}
              className="hv-btn hv-btn-ghost text-xs disabled:opacity-40"
              data-testid="proj-link-select-all"
            >Select all</button>
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              disabled={selected.size === 0}
              className="hv-btn hv-btn-ghost text-xs disabled:opacity-40"
              data-testid="proj-link-select-none"
            >Clear</button>
            <button
              type="button"
              onClick={() => {
                const ents = results.filter((r) => selected.has(r.id));
                if (ents.length && onPickMany) onPickMany(ents);
              }}
              disabled={selected.size === 0}
              className="hv-btn hv-btn-primary text-xs disabled:opacity-40"
              data-testid="proj-link-multi-submit"
            >Link {selected.size || ""} selected</button>
          </div>
        </div>
      </div>

      {/* Results list — fills the rest of the viewport */}
      <div className="flex-1 overflow-y-auto px-4 py-3" data-testid="proj-link-results">
        {results.length === 0 && !searching ? (
          <div className="hv-card p-8 text-center text-[#647087] max-w-md mx-auto">
            <Inbox className="w-7 h-7 mx-auto mb-2 opacity-40" />
            <div className="text-sm">No {kind.replace("_", " ")}s found.</div>
            <div className="text-xs mt-1">Try a different search term.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-3xl mx-auto">
            {results.map((r) => {
              const isSelected = selected.has(r.id);
              const toggleSelect = (e) => {
                e.stopPropagation();
                setSelected((prev) => {
                  const next = new Set(prev);
                  if (next.has(r.id)) next.delete(r.id); else next.add(r.id);
                  return next;
                });
              };
              return (
                <div
                  key={r.id}
                  className={`flex items-start gap-2 p-3 rounded border transition-colors ${
                    isSelected
                      ? "border-[#1FB6C1] bg-[#1FB6C1]/[0.08]"
                      : "border-white/10 hover:border-[#1FB6C1]/50 hover:bg-[#1FB6C1]/[0.04]"
                  }`}
                  data-testid={`proj-link-result-${r.id}`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={toggleSelect}
                    className="mt-1 shrink-0"
                    data-testid={`proj-link-result-${r.id}-check`}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <button
                    type="button"
                    onClick={() => onPick(r)}
                    className="text-left text-sm flex-1 min-w-0"
                    data-testid={`proj-link-result-${r.id}-click`}
                  >
                    <div className="font-medium text-white truncate">{labelFor(r)}</div>
                    {r.project_id && (
                      <div className="text-[10px] text-amber-400 mt-0.5 inline-flex items-center gap-1">
                        <AlertTriangle className="w-2.5 h-2.5" />
                        Already linked to project {r.project_id?.slice(0, 8)} — picking will move it here.
                      </div>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}


/** Wave 27.2 Phase B — Procurement radar card. Lazy-loads suggestions
 *  the moment it scrolls into view; clicking a supplier suggestion opens
 *  the Supplier 360 page.
 */
function ProcurementRadarCard({ projectId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanned, setScanned] = useState(false);

  const scan = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/procurement-radar`);
      setData(data);
    } catch {
      toast.error("Procurement radar unavailable.");
    } finally { setLoading(false); setScanned(true); }
  };

  return (
    <div className="hv-card !p-3" data-testid="proj-procurement-radar">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 font-display font-semibold text-sm text-[#1FB6C1]">
          <Radar className="w-3.5 h-3.5" />
          Procurement radar
        </div>
        <button
          type="button"
          onClick={scan}
          disabled={loading}
          className="hv-btn hv-btn-ghost text-[11px] disabled:opacity-50"
          data-testid="proj-radar-scan"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Radar className="w-3 h-3" />}
          {scanned ? "Rescan" : "Scan"}
        </button>
      </div>
      {!scanned && (
        <div className="text-[11px] text-[#647087] italic">
          Hidro can read accepted quotes + low-stock inventory and suggest who to order from. Tap Scan.
        </div>
      )}
      {scanned && data && !data.has_suggestions && (
        <div className="text-[11px] text-[#647087] italic">
          No procurement suggestions right now — nothing missing from quotes or low in inventory.
        </div>
      )}
      {scanned && data?.low_stock_items?.length > 0 && (
        <div className="mb-2">
          <div className="text-[9px] uppercase tracking-wider text-amber-300 mb-1">Low stock</div>
          <ul className="space-y-0.5">
            {data.low_stock_items.slice(0, 5).map((item) => (
              <li key={item.id} className="text-[11px] text-[#a0aabf] flex justify-between">
                <span className="truncate">{item.name}</span>
                <span className="font-mono text-rose-400 shrink-0">
                  {item.stock_on_hand} / {item.reorder_level}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {scanned && data?.suggestions?.length > 0 && (
        <div>
          <div className="text-[9px] uppercase tracking-wider text-[#1FB6C1] mb-1">Suggested suppliers</div>
          <ul className="space-y-1.5">
            {data.suggestions.map((s) => (
              <li key={s.keyword} className="text-[11px]">
                <span className="text-[#647087]">For </span>
                <span className="font-medium text-white">{s.keyword}</span>
                <div className="flex flex-wrap gap-1 mt-0.5">
                  {s.suggested_suppliers.map((sup) => (
                    <Link
                      key={sup.supplier_id}
                      to={`/suppliers/${sup.supplier_id}/360`}
                      className="text-[10px] px-1.5 py-0.5 rounded-full border border-[#A78BFA]/30 text-[#A78BFA] hover:bg-[#A78BFA]/[0.08] inline-flex items-center gap-1"
                      data-testid={`proj-radar-supplier-${sup.supplier_id}`}
                    >
                      <Truck className="w-2.5 h-2.5" />
                      {sup.supplier_name}
                      <span className="text-[#647087]">· used {sup.use_count}×</span>
                    </Link>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ---------------- Project Documents card ------------------------------
 * Bidirectional Customer ↔ Project document linking (Dirk 2026-02).
 *
 * Backend:
 *   GET    /api/projects/{pid}/documents          — already-linked docs
 *   GET    /api/projects/{pid}/documents/available— customer docs not yet linked
 *   POST   /api/projects/{pid}/documents/{did}/link
 *   DELETE /api/projects/{pid}/documents/{did}/link
 *   POST   /api/customers/{cid}/profile-documents  (with project_ids=pid)
 *
 * The "Documents" card lives in the left column under the linked entities.
 * Mobile: full width, stacked. Designed to pass the 360px mobile contract.
 * --------------------------------------------------------------------- */
function ProjectDocumentsCard({ projectId, customerId, isTech }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/documents`);
      setDocs(Array.isArray(data) ? data : []);
    } catch {
      // ignore — backend will 403 for unrelated users; we just show empty
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const onUpload = async (fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error("File must be ≤ 50 MB.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/customers/${customerId}/profile-documents`, fd, {
        params: { category: "proposal", project_ids: projectId },
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90_000,
      });
      toast.success("Document uploaded and linked.");
      // 2026-02-18 Round D2 — also queue OCR review for this project doc.
      const ocrCount = await kickOffAutoOcrBatch([file], {
        customer_id: customerId,
        project_id: projectId,
        source: "project_doc",
      });
      if (ocrCount > 0) toast.success("Queued for OCR review.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  const onUnlink = async (docId) => {
    setBusy(true);
    try {
      await api.delete(`/projects/${projectId}/documents/${docId}/link`);
      toast.success("Unlinked from project.");
      await load();
    } catch {
      toast.error("Couldn't unlink.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hv-card !p-3" data-testid="proj-documents-card">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="font-display font-semibold text-sm text-[#a0aabf] inline-flex items-center gap-1.5">
          <FolderOpen className="w-3.5 h-3.5 text-[#A78BFA]" /> Documents
          {docs.length > 0 && (
            <span className="text-[10px] text-[#647087] font-mono">· {docs.length}</span>
          )}
        </div>
        {!isTech && (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setPicker(true)}
              disabled={busy}
              className="hv-btn hv-btn-ghost text-[11px] !py-1 !px-2"
              data-testid="proj-doc-link-existing-btn"
              title="Link an existing customer document"
            >
              <LinkIcon className="w-3 h-3" /> Link existing
            </button>
            <label
              className={`hv-btn hv-btn-primary text-[11px] !py-1 !px-2 cursor-pointer ${busy ? "opacity-60 pointer-events-none" : ""}`}
              data-testid="proj-doc-upload-btn"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
              Upload
              <input
                type="file"
                className="hidden"
                onChange={(e) => onUpload(e.target.files)}
                accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx,.csv,.txt"
                data-testid="proj-doc-upload-input"
              />
            </label>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-xs text-[#647087] inline-flex items-center gap-1.5 py-2">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading…
        </div>
      ) : docs.length === 0 ? (
        <div className="text-xs text-[#647087] py-1" data-testid="proj-doc-empty">
          No documents linked yet. Drawings, warranties, signed contracts —
          link them here so they're one click away from the project.
        </div>
      ) : (
        <ul className="space-y-1.5" data-testid="proj-doc-list">
          {docs.map((d) => (
            <li
              key={d.id}
              className="flex items-center gap-2 bg-white/[0.02] hover:bg-white/[0.04] rounded px-2 py-1.5 text-xs"
              data-testid={`proj-doc-row-${d.id}`}
            >
              <FileText className="w-3.5 h-3.5 text-[#a0aabf] shrink-0" />
              <a
                href={d.url || "#"}
                target="_blank"
                rel="noreferrer"
                className="flex-1 min-w-0 truncate text-[#dde3ec] hover:text-[#1FB6C1]"
                title={d.filename}
              >
                {d.filename}
              </a>
              <span className="hidden sm:inline text-[9px] uppercase tracking-wider text-[#647087]">
                {(d.category || "").replace("_", " ")}
              </span>
              {d.url && (
                <a
                  href={d.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#647087] hover:text-white p-0.5"
                  data-testid={`proj-doc-open-${d.id}`}
                  aria-label="Open"
                  title="Open"
                >
                  <Download className="w-3 h-3" />
                </a>
              )}
              {!isTech && (
                <button
                  type="button"
                  onClick={() => onUnlink(d.id)}
                  disabled={busy}
                  className="text-[#647087] hover:text-rose-400 p-0.5"
                  data-testid={`proj-doc-unlink-${d.id}`}
                  aria-label="Unlink"
                  title="Unlink from project"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {picker && (
        <ProjectDocumentLinkPicker
          projectId={projectId}
          onClose={() => setPicker(false)}
          onLinked={async () => {
            await load();
            setPicker(false);
          }}
        />
      )}

      {/* Iter 428.p6 — Reports & Technical (customer_documents) scoped
          to this project. When a report is generated on the Engineering
          Desk and linked to a project, it surfaces here AND on the
          customer's Documents tab — the same one-doc-two-homes flow
          as manually-linked sales docs. */}
      <div className="mt-3">
        <ReportsTechnicalSection
          projectId={projectId}
          emptyHint="No reports yet — generate one from the Engineering Desk and link it to this project."
        />
      </div>
    </div>
  );
}

function ProjectDocumentLinkPicker({ projectId, onClose, onLinked }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}/documents/available`);
        setItems(Array.isArray(data) ? data : []);
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId]);

  const filtered = items.filter((d) => {
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return (
      (d.filename || "").toLowerCase().includes(s) ||
      (d.description || "").toLowerCase().includes(s) ||
      (d.category || "").toLowerCase().includes(s)
    );
  });

  const link = async (docId) => {
    setBusyId(docId);
    try {
      await api.post(`/projects/${projectId}/documents/${docId}/link`);
      toast.success("Linked to project.");
      onLinked();
    } catch {
      toast.error("Couldn't link.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1100] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 hv-safe-top hv-safe-bottom"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="proj-doc-picker"
    >
      <div className="w-full max-w-lg bg-[#121419] border border-white/10 rounded-t-2xl sm:rounded-lg overflow-hidden flex flex-col max-h-[calc(100dvh-env(safe-area-inset-top)-env(safe-area-inset-bottom))]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <h3 className="font-display text-base font-bold">Link an existing document</h3>
          <button onClick={onClose} className="text-[#647087] hover:text-white" data-testid="proj-doc-picker-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-3 border-b border-white/10">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#647087]" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search the customer's documents…"
              className="hv-input text-sm pl-7 w-full"
              data-testid="proj-doc-picker-search"
              autoFocus
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
          {loading ? (
            <div className="text-xs text-[#647087] inline-flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-xs text-[#647087]" data-testid="proj-doc-picker-empty">
              No documents available to link. Upload a new one from the project Documents card, or add it from the customer profile first.
            </div>
          ) : filtered.map((d) => (
            <div
              key={d.id}
              className="flex items-center gap-2 bg-white/[0.02] hover:bg-white/[0.05] rounded px-2 py-2 text-xs"
              data-testid={`proj-doc-picker-row-${d.id}`}
            >
              <FileText className="w-3.5 h-3.5 text-[#a0aabf] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="truncate text-[#dde3ec]" title={d.filename}>{d.filename}</div>
                <div className="text-[10px] text-[#647087] uppercase tracking-wider">
                  {(d.category || "").replace("_", " ")}
                  {d.description ? <span className="normal-case tracking-normal text-[#a0aabf]"> · {d.description}</span> : null}
                </div>
              </div>
              <button
                type="button"
                onClick={() => link(d.id)}
                disabled={busyId === d.id}
                className="hv-btn hv-btn-primary text-[11px] !py-1 !px-2"
                data-testid={`proj-doc-picker-link-${d.id}`}
              >
                {busyId === d.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <LinkIcon className="w-3 h-3" />}
                Link
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
